/* ══ v14.2 (b): SHORT ACTION NUMBERS ═══════════════════════════════════════════════════
   Actions were the one register never converted by v14 (W3b), whose own invariant is that
   "the ugly six-figure block ids never reach a screen". Writer-scoped minting made ids like
   #927191000001, and they were printed on cards, chips, the board, dialogs, the dashboard,
   the breakdown lists and the text you copy into an email.

   ⚠ THE LABEL IS DERIVED; THE LINK IS STORED BY REAL ID. Nothing here is ever persisted.
   parentId, dependsOn and relatedTo still hold real ids, so renumbering moves a LABEL on
   screen and cannot touch a relationship. The ONE thing that can go stale is a number
   written down OUTSIDE the app - in an email or a message - and he accepted that knowingly:
   one refers to an action by what it IS, not by a twelve-digit number nobody can read aloud.

   Same derivation the other registers use (_mintDisplayOrd): position within the live,
   id-sorted collection, so every machine shows the same label for the same record and a
   deleted row leaves no hole. Top-level actions are counted among THEMSELVES, so the
   sequence is 1..n with no gaps where sub-actions sit. */
function _topLevelActionsSorted() {
  return getAllActions()
    .filter(function (a) { const e = edits[a.id]; return !e || e.parentId == null; })
    .sort(function (x, y) { return _mergeCmpKey(x.id, y.id); });
}

/* The reverse of displayActionId: what the USER TYPED -> a REAL action id.
   ⚠ THIS IS THE ONLY PLACE A LABEL IS INTERPRETED, and it is what makes the whole scheme
   safe: every caller stores what this returns, never the number that was typed.
   Accepts "4", "#4", "#4.2" (a sub-action) and trailing junk ("#4: whatever"), matching
   what parseDependencyInput accepted before. Returns null when nothing matches. */
function actionIdFromDisplay(value) {
  const m = String(value == null ? '' : value).trim().match(/^#?(\d+)(?:\.(\d+))?/);
  if (!m) return null;
  const top = _topLevelActionsSorted();
  const n = Number(m[1]);
  if (!(n >= 1 && n <= top.length)) return null;
  const parent = top[n - 1];
  if (m[2] == null) return parent.id;
  const subs = getSubActionIds(parent.id);
  const k = Number(m[2]);
  return (k >= 1 && k <= subs.length) ? subs[k - 1] : null;
}

/* v14.2 (b): "#4 — " for a live action, and NOTHING for one that has been deleted, so a
   pasted line reads "- Fix the roof" rather than "- #? — Fix the roof". */
function _refPrefix(id) {
  const lbl = displayActionIdText(id);
  return lbl ? (lbl + ' \u2014 ') : '';
}

/* The same label, for TEXT THAT LEAVES THE APP (copied minutes, the agenda). An action that
   no longer exists contributes NOTHING rather than "#?", because those lines are pasted into
   an email where "#?" reads as a bug - and the description beside it already says which
   action it was. */
function displayActionIdText(id) {
  const lbl = displayActionId(id);
  return lbl === '#?' ? '' : lbl;
}

/* Display ID for an action — "#18" for top-level, "#18.1" / "#18.2" for sub-actions.
   Position uses sibling-rank within the parent's sub-action list (1-indexed).
   Stable: always derived fresh from current state — no stored numbering.
   ⚠ v14.2 (b): the top-level part is now a SHORT ORDINAL too, not the raw minted id. */
function displayActionId(id) {
  const e = edits[id];
  if (!e || e.parentId == null) {
    const top = _topLevelActionsSorted();
    const i = top.findIndex(function (a) { return a.id === id; });
    /* '#?' rather than the raw id: an action we cannot place has no short number, and the
       twelve-digit one is exactly what this change exists to keep off the screen. */
    return i === -1 ? '#?' : ('#' + (i + 1));
  }
  const top = _topLevelActionsSorted();
  const pi = top.findIndex(function (a) { return a.id === e.parentId; });
  const siblings = getSubActionIds(e.parentId);
  const idx = siblings.indexOf(id);
  return '#' + (pi === -1 ? '?' : (pi + 1)) + (idx >= 0 ? '.' + (idx + 1) : '.?');
}

/* v2.90: re-parent an existing action (make it a sub-action of parentId, or un-nest if null).
   Validates via canSetParent; silently no-ops on invalid moves (defensive — UI also gates). */
function setParent(actionId, parentId) {
  if (!edits[actionId]) return;
  if (!canSetParent(actionId, parentId)) return;
  edits[actionId].parentId = (typeof parentId === 'number') ? parentId : null;
  markDirty();
  refreshAll();
}

/* v2.90: un-nest a sub-action (set parentId to null). */
function removeParent(actionId) {
  setParent(actionId, null);
}

/* v2.90: parse #N input string and invoke setParent. Used by the parent-chip-input field. */
function handleParentInput(actionId, inputEl) {
  const raw = (inputEl.value || '').trim();
  if (!raw) { inputEl.value = ''; return; }
  /* v14.2 (b): a SHORT number, and only a top-level one - single-level nesting means a
     sub-action ("#4.2") can never be a parent, so the '.n' form is refused here by design. */
  const m = raw.match(/^#?(\d+)$/);
  /* ── v13.1 (A47): say why, like the two inputs either side of it ──────────────────────
     Both neighbouring chip inputs explain a rejection (handleDependencyInput toasts "Enter
     an action ID (e.g. #5)"). This one wiped what you typed and said nothing — identical
     behaviour for a typo, a non-existent action and a parent that would create a cycle, so
     the only signal was the box going empty and nothing happening. */
  if (!m) {
    showToast('Enter an action number you can see on a card (e.g. #5)', { type: 'warn' });
    inputEl.value = '';
    return;
  }
  /* v14.2 (b): resolve the typed LABEL to the real id, and STORE THAT. The messages echo
     what he typed, never the id behind it. */
  const typed = '#' + m[1];
  const targetId = actionIdFromDisplay(m[1]);
  if (targetId == null) {
    showToast('There is no action ' + typed, { type: 'warn' });
    inputEl.value = '';
    return;
  }
  if (!canSetParent(actionId, targetId)) {
    /* canSetParent refuses three different things; name the one that applies rather than
       inventing a reason (the v10.4.2 rule). */
    showToast(targetId === actionId
      ? 'An action cannot be its own parent'
      : (typed + ' cannot be the parent — it would nest more than one level, or close a loop'),
      { type: 'warn' });
    inputEl.value = '';
    return;
  }
  setParent(actionId, targetId);
  inputEl.value = '';
}

/* v2.40: add/remove single label on an action.
   addLabel trims, dedupes, and ignores empty strings. */
function addLabel(id, value) {
  const v = (value || '').trim();
  if (!v) return;
  const e = edits[id];
  if (!e) return;
  if (!Array.isArray(e.labels)) e.labels = [];
  if (e.labels.includes(v)) return;  /* dedupe */
  e.labels.push(v);
  sessionEditedActionIds.add(id);
  markDirty();
  refreshAll();
  updateLabelDatalist();
}
function removeLabel(id, index) {
  const e = edits[id];
  if (!e || !Array.isArray(e.labels)) return;
  if (index < 0 || index >= e.labels.length) return;
  e.labels.splice(index, 1);
  sessionEditedActionIds.add(id);
  markDirty();
  refreshAll();
  updateLabelDatalist();
}

function updateLabelDatalist() {
  const cats = new Set();
  const all = [...ACTIONS, ...newActions].filter(a => !deletedIds.has(a.id));
  /* v2.40: collect labels from arrays across all actions */
  all.forEach(a => {
    const ls = edits[a.id] && edits[a.id].labels;
    if (Array.isArray(ls)) ls.forEach(l => { if (l && typeof l === 'string') cats.add(l); });
  });
  const dl = document.getElementById('labelList');
  if (dl) dl.innerHTML = [...cats].sort().map(c => `<option value="${esc(c)}"></option>`).join('');
  if (_actionLabelContext) _actionLabelContext.refresh(true);
}

/* v3.3: stakeholder-name suggestions for action owner + requestor + risk owner.
   Populates <datalist id="stakeholderList"> from STAKEHOLDERS[]. Free text is
   retained — these are autocomplete suggestions only, the underlying field
   stays a plain string. Call after any stakeholder mutation. */
// v6.3: per-row attendee NAME suggestions, filtered by that row's Organisation.
// Each attendee row owns its own <datalist id="attNames_<sessionId>_<rowIndex>">, built
// from stakeholders whose .organisation matches the row's Organisation (case-insensitive,
// trimmed); an empty Organisation shows ALL names. The global stakeholderList is
// deliberately NOT touched here — it still feeds the action owner / requestor / risk
// owner pickers, which are intentionally unfiltered.
function _attendeeNameOptionsHTML(company) {
  const want = (typeof company === 'string' ? company : '').trim().toLowerCase();
  const names = (Array.isArray(STAKEHOLDERS) ? STAKEHOLDERS : [])
    .filter(s => s && typeof s.name === 'string' && s.name.trim() &&
      (!want || (typeof s.organisation === 'string' && s.organisation.trim().toLowerCase() === want)))
    .map(s => s.name.trim());
  return [...new Set(names)].sort().map(n => `<option value="${esc(n)}"></option>`).join('');
}
function refilterAttendeeNames(sessionId, rowIndex, company) {
  const dl = document.getElementById('attNames_' + sessionId + '_' + rowIndex);
  if (dl) dl.innerHTML = _attendeeNameOptionsHTML(company);
}
function updateStakeholderDatalist() {
  const names = (Array.isArray(STAKEHOLDERS) ? STAKEHOLDERS : [])
    .map(s => (s && typeof s.name === 'string') ? s.name.trim() : '')
    .filter(Boolean);
  const dl = document.getElementById('stakeholderList');
  if (dl) dl.innerHTML = [...new Set(names)].sort().map(n => `<option value="${esc(n)}"></option>`).join('');
}

/* v3.4: company suggestions for attendee company (Minutes) + stakeholder company
   (modal). Source is the Stakeholders register ONLY — distinct non-empty
   s.organisation values — so company spellings stay consistent with the register
   (no drift from free-typed attendee rows or the brand). Free text retained. */
function updateOrganisationDatalist() {
  const companies = (Array.isArray(STAKEHOLDERS) ? STAKEHOLDERS : [])
    .map(s => (s && typeof s.organisation === 'string') ? s.organisation.trim() : '')
    .filter(Boolean);
  const dl = document.getElementById('organisationList');
  if (dl) dl.innerHTML = [...new Set(companies)].sort().map(c => `<option value="${esc(c)}"></option>`).join('');
}

function applyStatFilter(key) {
  /* v2.90: pill-strip behavior. Total pill is the default-active state.
     Shortcut pills (agenda/overdue/blocked/high-open) are mutually exclusive with each other
     and toggle off when clicked while already active (returning to all/Total).
     Click-Total-while-shortcut-active = clear shortcut. */
  if (activeStatFilter === key && key !== 'all') key = 'all';
  activeStatFilter = key;
  /* Legacy mapping: the old stat-card keys (in-progress/not-started/completed) are
     no longer used by the pill strip, but we keep them no-op-mapped to 'all' for
     graceful handling of any saved uiState referencing them. */
  switch (key) {
    case 'all':
    case 'agenda':
    case 'overdue':
    case 'blocked':
      /* These do not change the underlying status/priority filters — they apply as post-filters. */
      activeStatus = 'all';
      activePriority = 'all';
      break;
    case 'high-open':
      activeStatus = 'all';
      activePriority = 'high';
      break;
    case 'in-progress':
    case 'not-started':
    case 'completed':
      /* Legacy — gracefully map to all (those tiles no longer exist) */
      activeStatFilter = 'all';
      activeStatus = 'all';
      activePriority = 'all';
      break;
  }
  // Sync the top filter button rows' active state to match
  document.querySelectorAll('#statusFilters .filter-btn').forEach(b => b.classList.toggle('active', b.dataset.filter === activeStatus));
  document.querySelectorAll('#priorityFilters .filter-btn').forEach(b => b.classList.toggle('active', b.dataset.priority === activePriority));
  refreshAll();
}

/* ── ⚠⚠ v16.8 (W4): A DELETION MARKER IS NOT AN ACTION, AND THIS IS THE ONE PLACE TO SAY SO
   A tombstone is `{ id, del, rev, writer }` and nothing else — no desc, no status, no due
   date. SWEEP-2 found one being COUNTED in the WBS coverage denominator and then OFFERED for
   assignment: "2 / 3 … 1 unassigned: #3 (untitled) → assign…", inviting the user to attach a
   workstream to a record that does not exist. Hand-computed 2 of 5; the app said 2 of 6.
   ⚠⚠ FIXED AT THE SHARED ACCESSOR, NOT AT THE COVERAGE STRIP, AND THAT WAS A REAL CHOICE.
   There are 27 call sites and P6 measured one. Filtering only the measured one would leave 26
   consumers reading ghost records — and v14 (W4a) already states the intent outright:
   tombstones "must NOT sit in the arrays the app renders from, or every renderer, counter and
   export in the shell would need a `!r.del` filter and the ones that were missed would show
   ghost rows". **This accessor IS that one place.**
   ⚠ P6 was careful that the arithmetic is unconditionally wrong while REACHABILITY was not
   established. That asymmetry argues for the accessor rather than against it: the cheap fix
   also covers the paths nobody measured. On healthy data it is a no-op — _revHarvestTombstones
   pulls them out at adopt time, and this is the second line of defence for the window before
   that runs, which is exactly the shape the merge produces. */
function getAllActions() { return [...ACTIONS, ...newActions].filter(a => a && !a.del && !deletedIds.has(a.id)); }

function renderStats() {
  /* v2.90: render the pill strip — Total + Agenda · Overdue · Blocked · High open.
     Pills with count = 0 are hidden. Per-pill counts are computed from current edits state. */
  const data = getAllActions().map(a => ({...a,...edits[a.id]}));
  const total = data.length;
  const todayStr = _todayISO();
  const isOpen = (a) => a.status !== 'Completed' && a.status !== 'Cancelled';
  const agendaCount  = data.filter(a => a.flagged === true).length;
  const overdueCount = data.filter(a => isOpen(a) && a.due && a.due < todayStr).length;
  const blockedCount = data.filter(a => isOpen(a) && hasOpenBlocker(a.id)).length;
  const highOpenCount = data.filter(a => a.priority === 'High' && isOpen(a)).length;
  /* Build pill HTML. Total pill is always shown. Other pills hidden when count=0. */
  const active = (k) => k === activeStatFilter ? ' active' : '';
  const totalActive = (!activeStatFilter || activeStatFilter === 'all') ? ' active' : '';
  const iconAgenda  = `<span class="pill-icon"><svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="1.5" y="1.5" width="11" height="11" rx="2"/><path d="M4 7L6.2 9.2L10 5"/></svg></span>`;
  const iconOverdue = `<span class="pill-icon"><svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><circle cx="7" cy="7" r="5.5"/><path d="M7 4v3l2 1.5"/></svg></span>`;
  const iconBlocked = `<span class="pill-icon"><svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="7" cy="7" r="5.5"/><path d="M3.5 3.5l7 7"/></svg></span>`;
  const iconHigh    = `<span class="pill-icon"><svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M7 12V2M3 6l4-4 4 4"/></svg></span>`;
  let html = `<button class="pill pill-total${totalActive}" onclick="applyStatFilter('all')" title="Show all actions">Total<span class="pill-count">${total}</span></button>`;
  /* Separator only if there's at least one colored pill visible */
  const anyShortcut = agendaCount > 0 || overdueCount > 0 || blockedCount > 0 || highOpenCount > 0;
  if (anyShortcut) html += `<span class="pill-sep"></span>`;
  if (agendaCount > 0) {
    html += `<button class="pill pill-agenda${active('agenda')}" onclick="applyStatFilter('agenda')" title="Filter to actions on the agenda">${iconAgenda}Agenda<span class="pill-count">${agendaCount}</span></button>`;
  }
  if (overdueCount > 0) {
    html += `<button class="pill pill-overdue${active('overdue')}" onclick="applyStatFilter('overdue')" title="Filter to overdue open actions">${iconOverdue}Overdue<span class="pill-count">${overdueCount}</span></button>`;
  }
  if (blockedCount > 0) {
    html += `<button class="pill pill-blocked${active('blocked')}" onclick="applyStatFilter('blocked')" title="Filter to blocked open actions">${iconBlocked}Blocked<span class="pill-count">${blockedCount}</span></button>`;
  }
  if (highOpenCount > 0) {
    html += `<button class="pill pill-high${active('high-open')}" onclick="applyStatFilter('high-open')" title="Filter to high-priority open actions">${iconHigh}High open<span class="pill-count">${highOpenCount}</span></button>`;
  }
  document.getElementById('statsRow').innerHTML = html;
}

function updateCounts() {
  const data=getAllActions().map(a=>({...a,...edits[a.id]}));
  /* v2.19: include on-hold and cancelled counts */
  const c={all:data.length,'in-progress':0,'not-started':0,'on-hold':0,completed:0,cancelled:0};
  data.forEach(a=>{const k=statusKey(a.status);if(c[k]!==undefined)c[k]++;});
  document.getElementById('cnt-all').textContent=c.all;
  document.getElementById('cnt-in-progress').textContent=c['in-progress'];
  document.getElementById('cnt-not-started').textContent=c['not-started'];
  const cntOnHold = document.getElementById('cnt-on-hold');
  if (cntOnHold) cntOnHold.textContent=c['on-hold'];
  document.getElementById('cnt-completed').textContent=c.completed;
  const cntCancelled = document.getElementById('cnt-cancelled');
  if (cntCancelled) cntCancelled.textContent=c.cancelled;
}

/* ── ACTIONS RENDER ── */
/* v2.9: smart expand/collapse-all toggles. View-state only — do NOT mark dirty.
   "Majority wins" threshold: if MORE THAN HALF of cards are expanded, click
   collapses all; otherwise click expands all. So with e.g. 1 of 24 expanded,
   one click expands the other 23 (instead of needing two clicks to undo and re-do).
   Operates directly on DOM classList (no re-render needed). */
function toggleAllActions() {
  const cards = document.querySelectorAll('#actionsList .action-card');
  if (!cards.length) return;
  const expandedCount = [...cards].filter(c => c.classList.contains('expanded')).length;
  const mostlyExpanded = expandedCount > cards.length / 2;
  cards.forEach(c => c.classList.toggle('expanded', !mostlyExpanded));
  const btn = document.getElementById('expandActionsBtn');
  if (btn) btn.textContent = mostlyExpanded ? 'Expand all' : 'Collapse all';
}
function toggleAllSessions() {
  const cards = document.querySelectorAll('#sessionList .session-card');
  if (!cards.length) return;
  const expandedCount = [...cards].filter(c => c.classList.contains('expanded')).length;
  const mostlyExpanded = expandedCount > cards.length / 2;
  cards.forEach(c => c.classList.toggle('expanded', !mostlyExpanded));
  const lbl = document.querySelector('#expandSessionsBtn span');
  if (lbl) lbl.textContent = mostlyExpanded ? 'Expand all' : 'Collapse all';
}
/* Keep button labels in sync with DOM after re-renders (which may change which cards exist).
   Same threshold logic — label tells user what the next click will do. */
/* v2.16: enable/disable the Duplicate-latest button based on whether sessions exist */
function syncDuplicateBtnState() {
  const btn = document.getElementById('duplicateLatestBtn');
  if (btn) btn.disabled = SESSIONS.length === 0;
}

function syncExpandToggleLabels() {
  const aCards = document.querySelectorAll('#actionsList .action-card');
  const aBtn = document.getElementById('expandActionsBtn');
  if (aBtn && aCards.length) {
    const expandedCount = [...aCards].filter(c => c.classList.contains('expanded')).length;
    const mostlyExpanded = expandedCount > aCards.length / 2;
    aBtn.textContent = mostlyExpanded ? 'Collapse all' : 'Expand all';
  }
  const sCards = document.querySelectorAll('#sessionList .session-card');
  const sLbl = document.querySelector('#expandSessionsBtn span');
  if (sLbl && sCards.length) {
    const expandedCount = [...sCards].filter(c => c.classList.contains('expanded')).length;
    const mostlyExpanded = expandedCount > sCards.length / 2;
    sLbl.textContent = mostlyExpanded ? 'Collapse all' : 'Expand all';
  }
}

function toggleHideCompleted() {
  hideCompleted = !hideCompleted;
  const btn = document.getElementById('hideCompletedBtn');
  if (btn) {
    /* v2.19: label says 'closed' now — covers both Completed and Cancelled */
    btn.textContent = hideCompleted ? 'Show closed' : 'Hide closed';
    btn.classList.toggle('active', hideCompleted);
  }
  refreshAll();
}

function getFiltered() {
  /* v2.90: cache today's date once per call for OVERDUE comparison */
  const todayStr = _todayISO();
  return getAllActions().filter(a => {
    const e = edits[a.id];
    /* v2.7: hide-completed toggle — but a stat card filter for 'completed' OVERRIDES it.
       The override condition is naturally met because activeStatus would equal 'completed'
       in that case, so we explicitly skip the hide-completed filter when that happens. */
    /* v2.19: hideCompleted now hides both Completed AND Cancelled (toggle renamed to 'Hide closed').
       Skip the hide when user has explicitly filtered to one of these statuses via stat card/button. */
    if (hideCompleted && activeStatus !== 'completed' && activeStatus !== 'cancelled'
        && (statusKey(e.status) === 'completed' || statusKey(e.status) === 'cancelled')) return false;
    if (activeStatus !== 'all' && statusKey(e.status) !== activeStatus) return false;
    if (activePriority !== 'all' && e.priority.toLowerCase() !== activePriority) return false;
    /* v2.90: pill-strip filters (agenda / overdue / blocked / high-open).
       Each pill's filter is an additional AND-condition. Total/'all' passes everything. */
    if (activeStatFilter === 'agenda') {
      if (e.flagged !== true) return false;
    } else if (activeStatFilter === 'overdue') {
      if (e.status === 'Completed' || e.status === 'Cancelled') return false;
      if (!e.due || e.due >= todayStr) return false;
    } else if (activeStatFilter === 'blocked') {
      if (e.status === 'Completed' || e.status === 'Cancelled') return false;
      if (!hasOpenBlocker(a.id)) return false;
    } else if (activeStatFilter === 'high-open') {
      /* Existing v2.22 behavior preserved: priority filter already set to 'high';
         additionally exclude completed (cancelled is also closed). */
      if (e.status === 'Completed' || e.status === 'Cancelled') return false;
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      /* v2.5: also search category */

      // Use edited values, falling back to original — matches what user actually sees
      const desc = (actField(e, a, 'desc') || '').toLowerCase();
      const owner = (actField(e, a, 'owner') || '').toLowerCase();
      const source = (actField(e, a, 'source') || '').toLowerCase();
      const details = (e.details || a.details || '').toLowerCase();
      /* v2.40: search includes labels (joined into one searchable string) */
      const labelsStr = (Array.isArray(e.labels) ? e.labels.join(' ') : '').toLowerCase();
      const requestor = (e.requestor || '').toLowerCase();  /* v2.12 */
      const updateText = e.updates.some(u => u.text.toLowerCase().includes(q));
      /* v2.22: also search progress field (e.g. "50" matches progress=50) */
      const progressStr = (typeof e.progress === 'number') ? String(e.progress) : '';
      if (!desc.includes(q) && !owner.includes(q) && !source.includes(q) &&
          !details.includes(q) && !labelsStr.includes(q) && !requestor.includes(q) && !updateText && !String(a.id).includes(q) && !progressStr.includes(q)) return false;
    }
    return true;
  });
}

/* v2.10: helper — what to display on the direction toggle button.
   The label depends on the current sort field for clarity (it's clearer to say
   "Newest first" than "Descending" when sorting by date). */
function getSortDirLabel() {
  if (sortBy === 'id')       return sortDesc ? 'Highest first' : 'Lowest first';
  if (sortBy === 'date')     return sortDesc ? 'Newest first' : 'Oldest first';
  if (sortBy === 'priority') return sortDesc ? 'Low priority first' : 'High priority first';
  if (sortBy === 'status')   return sortDesc ? 'Done first' : 'Active first';
  /* v2.40: labels sort (alphabetic on first label) */
  if (sortBy === 'labels')   return sortDesc ? 'Z → A' : 'A → Z';
  /* v2.22: new sorts */
  if (sortBy === 'due')      return sortDesc ? 'Latest due first' : 'Earliest due first';
  if (sortBy === 'closed')   return sortDesc ? 'Latest closed first' : 'Earliest closed first';
  if (sortBy === 'progress') return sortDesc ? 'Most complete first' : 'Least complete first';
  return sortDesc ? 'Descending' : 'Ascending';
}
function updateSortDirBtn() {
  const lbl = document.getElementById('sortDirLabel');
  const ico = document.getElementById('sortDirIcon');
  if (lbl) lbl.textContent = getSortDirLabel();
  if (ico) ico.innerHTML = sortDesc
    ? '<path d="M6 1v9M3 7l3 3 3-3"/>'
    : '<path d="M6 11V2M3 5l3-3 3 3"/>';
}
function toggleSortDir() {
  sortDesc = !sortDesc;
  updateSortDirBtn();
  render();
  /* ⚠ v15.3 (c): render() paints the LIST ONLY. The board is painted by renderActionBoard(),
     so on the board the direction toggle changed the button's label and nothing else — his
     report: "sort by priority works but not the button high/low priority first". The sort
     FIELD dropdown got exactly this line in v14.2 (d) and this one was missed, though that
     wave's comment claimed BOTH controls had been done; the comment is corrected there. */
  if (actionsView === 'board') renderActionBoard();
}

function getSorted(arr) {
  /* v2.10: comparator-chain sort with smart tie-breakers.
     Direction toggle (sortDesc) flips ONLY the primary comparator.
     Tie-breakers always stay in their natural order. */
  const copy = [...arr];

  // Field-level comparators (always natural direction)
  const cmpId       = (a, b) => a.id - b.id;
  /* v13.1 (A30): read the EDITED date, like every other comparator here. This one alone
     read `a.date` — the last SAVED value — so changing an action's Date raised reordered
     nothing until you saved, and the list disagreed with the dates printed on its own cards. */
  const cmpDate     = (a, b) => (actField(edits[a.id], a, 'date') || '')
                          .localeCompare(actField(edits[b.id], b, 'date') || '');
  const cmpStatus   = (a, b) => STATUS_ORDER[edits[a.id].status] - STATUS_ORDER[edits[b.id].status];
  const cmpPriority = (a, b) => PRIORITY_ORDER[edits[a.id].priority] - PRIORITY_ORDER[edits[b.id].priority];
  const cmpDue = (a, b) => {
    const da = edits[a.id].due || '';
    const db = edits[b.id].due || '';
    if (!da && !db) return 0;
    /* v13.1 (A51): undated actions go last in BOTH directions — the behaviour this
       comparator's own comment always claimed. It returned a flat 1/-1, and the primary
       comparator is multiplied by the direction, so "Latest due first" flipped the nulls to
       the TOP: the actions with no due date led a list sorted by due date. Its three
       siblings below (category, closed date, progress) already neutralise the flip exactly
       this way; this one was the odd one out. */
    if (!da) return sortDesc ? -1 : 1;
    if (!db) return sortDesc ? 1 : -1;
    return da.localeCompare(db);
  };
  /* ⚠ v14.4 (d): `cmpCategory` was REMOVED here — the last of the four residual dead-code
     items the v10.3 W5 sweep did not claim, on his instruction 2026-08-15. Census, not a
     guess: it had exactly ONE occurrence in the whole shell — its own definition — and zero
     callers, because v2.40 replaced the action `category` field with `labels` and the sort
     menu has never offered a category option since. Its body read `edits[a.id].category`, a
     key nothing writes any more. */

  /* v2.22: closed-date comparator. Empty closedDate sorts LAST regardless of direction. */
  const cmpClosedDate = (a, b) => {
    const ca = edits[a.id].closedDate || '';
    const cb = edits[b.id].closedDate || '';
    if (!ca && !cb) return 0;
    if (!ca) return sortDesc ? -1 : 1;
    if (!cb) return sortDesc ? 1 : -1;
    return ca.localeCompare(cb);
  };
  /* v2.22: progress comparator. Null progress sorts LAST regardless of direction. */
  const cmpProgress = (a, b) => {
    const pa = edits[a.id].progress;
    const pb = edits[b.id].progress;
    const isNullA = (pa == null);
    const isNullB = (pb == null);
    if (isNullA && isNullB) return 0;
    if (isNullA) return sortDesc ? -1 : 1;
    if (isNullB) return sortDesc ? 1 : -1;
    return pa - pb;
  };

  // Tie-breaker chain by primary sort field
  let chain;
  if (sortBy === 'id')            chain = [cmpId];
  else if (sortBy === 'date')     chain = [cmpDate, cmpStatus, cmpPriority, cmpId];
  /* v2.10 refinement: when due date is null/empty or tied, fall through to date raised
     before ID. Older raise date wins (Option B: "items waiting longer rise"). */
  else if (sortBy === 'priority') chain = [cmpPriority, cmpStatus, cmpDue, cmpDate, cmpId];
  else if (sortBy === 'status')   chain = [cmpStatus, cmpPriority, cmpDue, cmpDate, cmpId];
  /* v2.19: category — group by category, then by priority/status/id within each group */
  /* v2.40: labels sort comparator — alphabetic on first label, empty arrays last */
  /* v2.90: sort by ALPHABETICALLY-FIRST label, not insertion-order first (fixes
     unpredictable sort when an action has multiple labels — order of insertion
     no longer affects sort position). */
  else if (sortBy === 'labels') chain = [(a, b) => {
    const la = (edits[a.id].labels || []);
    const lb = (edits[b.id].labels || []);
    if (la.length === 0 && lb.length === 0) return 0;
    if (la.length === 0) return sortDesc ? -1 : 1;
    if (lb.length === 0) return sortDesc ? 1 : -1;
    /* v2.90: pick alphabetically-first label from each, then compare. Insensitive
       to insertion order — if action has ["Vision One","Compliance"], the sort key
       is "Compliance" because it sorts alphabetically before "Vision One". */
    const firstA = [...la].sort()[0].toLowerCase();
    const firstB = [...lb].sort()[0].toLowerCase();
    return firstA.localeCompare(firstB);
  }, cmpPriority, cmpStatus, cmpId];
  /* v2.22: new sort modes */
  else if (sortBy === 'due')      chain = [cmpDue, cmpPriority, cmpStatus, cmpId];
  else if (sortBy === 'closed')   chain = [cmpClosedDate, cmpPriority, cmpStatus, cmpId];
  else if (sortBy === 'progress') chain = [cmpProgress, cmpStatus, cmpPriority, cmpId];
  else                            chain = [cmpId];

  // Direction multiplier — applied ONLY to the first (primary) comparator
  const dirMul = sortDesc ? -1 : 1;
  copy.sort((a, b) => {
    for (let i = 0; i < chain.length; i++) {
      const r = chain[i](a, b);
      if (r !== 0) return i === 0 ? r * dirMul : r;
    }
    return 0;
  });
  return copy;
}

/* v2.90: helpers for polished empty state.
   _buildActiveFilterPills returns HTML for each currently-active filter.
   clearAllActionFilters resets all action-list filters and re-renders. */
function _buildActiveFilterPills() {
  const pills = [];
  // Status filter (when not "all")
  if (activeStatus && activeStatus !== 'all') {
    const labelMap = {'not-started':'Not started','in-progress':'In progress','on-hold':'On hold','completed':'Completed','cancelled':'Cancelled'};
    pills.push(`<span class="empty-filter-pill">Status: ${esc(labelMap[activeStatus] || activeStatus)}</span>`);
  }
  // Priority filter
  if (activePriority && activePriority !== 'all') {
    const labelMap = {'high':'High','medium':'Medium','low':'Low'};
    pills.push(`<span class="empty-filter-pill">Priority: ${esc(labelMap[activePriority] || activePriority)}</span>`);
  }
  // v2.90 pill-strip filter (agenda/overdue/blocked/high-open)
  if (activeStatFilter && activeStatFilter !== 'all') {
    const pillLabelMap = {'agenda':'Agenda','overdue':'Overdue','blocked':'Blocked','high-open':'High open'};
    const lbl = pillLabelMap[activeStatFilter];
    if (lbl && !pills.some(p => p.includes(lbl))) {
      pills.push(`<span class="empty-filter-pill">${esc(lbl)}</span>`);
    }
  }
  // Search query
  if (searchQuery && searchQuery.trim()) {
    pills.push(`<span class="empty-filter-pill">Search: "${esc(searchQuery)}"</span>`);
  }
  // Hide completed toggle
  if (hideCompleted) {
    pills.push(`<span class="empty-filter-pill">Hide completed</span>`);
  }
  return pills.join('');
}

function clearAllActionFilters() {
  activeStatus = 'all';
  activePriority = 'all';
  activeStatFilter = 'all';
  searchQuery = '';
  hideCompleted = false;
  /* Sync DOM controls */
  const searchEl = document.getElementById('searchInput');
  if (searchEl) searchEl.value = '';
  document.querySelectorAll('#statusFilters .filter-btn').forEach(b => b.classList.toggle('active', b.dataset.filter === 'all'));
  document.querySelectorAll('#priorityFilters .filter-btn').forEach(b => b.classList.toggle('active', b.dataset.priority === 'all'));
  const hideBtn = document.getElementById('hideCompletedBtn');
  if (hideBtn) {
    hideBtn.classList.remove('active');
    hideBtn.textContent = 'Hide closed';
  }
  refreshAll();
}

function _renderActionCardHTML(a, opts) {
  /* v2.90: extracted from render() so sub-actions can render via the same code path.
     opts: { expandedIds, newIds, matchedIds, isSubAction } */
    const e=edits[a.id], sk=statusKey(e.status), pk=e.priority.toLowerCase();
    /* v2.19 item 8: is this action the currently-focused one? */
    const isFocused = appMeta.focusActionId === a.id;
    /* v2.21: open blocker check for visual marker */
    const isBlocked = hasOpenBlocker(a.id);
    const hasUpdate=e.updates.length>0, isExpanded=opts.expandedIds.has(a.id);
    /* v2.5: yellow highlight on card is session-local; persisted updates only drive the count pill */
    const isSessionEdited=sessionEditedActionIds.has(a.id);
    // Normalize raw a.status/a.priority before comparison — Teams imports use kebab-case while edits use display format
    const aStatus = STATUS_DISPLAY[a.status] || a.status;
    const aPriority = PRIORITY_DISPLAY[a.priority] || a.priority;
    /* v2.19 item 5: overdue when due date is past AND status is open
       (Not yet started, In progress, On hold — NOT Completed or Cancelled). */
    const _today = todayISO();
    const _statusForOverdue = e.status;
    const isOverdue = !!e.due && e.due < _today
                    && _statusForOverdue !== 'Completed'
                    && _statusForOverdue !== 'Cancelled';
    // Track ANY edited field. After save, ACTIONS gets rewritten with current edits → these all match again → dot disappears.
    const changed = e.status   !== aStatus
                 || e.priority !== aPriority
                 || e.due      !== (a.due || '')
                 || e.owner    !== (a.owner || '')
                 || e.desc     !== (a.desc || '')
                 || e.source   !== (a.source || '')
                 || e.date     !== (a.date || '')
                 || e.details  !== (a.details || '')
                 || JSON.stringify(Array.isArray(e.labels)?e.labels:[]) !== JSON.stringify(Array.isArray(a.labels)?a.labels:[])
                 || JSON.stringify(e.updates || []) !== JSON.stringify(a.updates || []);
    const updateLog=e.updates.map((u,i)=>`
      <div class="update-entry">
        <div class="update-entry-time">${esc(u.time)}</div>
        <div class="update-entry-text" title="Click to edit" onclick="editUpdate(${a.id},${i});event.stopPropagation()">${esc(u.text)}</div>
        <button class="update-entry-del" title="Delete note" onclick="deleteUpdate(${a.id},${i});event.stopPropagation()"><svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M2 2l8 8M10 2l-8 8"/></svg></button>
      </div>`).join('');
    return `
    <div class="action-card priority-${pk}${isExpanded?' expanded':''}${isSessionEdited?' has-update':''}${isFocused?' is-focused':''}${opts.isSubAction?' is-subaction':''}" id="card-${a.id}" data-id="${a.id}"${opts.isSubAction?' data-parent-id="'+e.parentId+'"':''}>
      <div class="action-header" onclick="toggleCard(${a.id})">
        <button class="agenda-flag${e.flagged?' flagged':''}" title="${e.flagged?'Remove from agenda':'Add to agenda'}" onclick="toggleFlag(${a.id});event.stopPropagation()">${AGENDA_FLAG_SVG}</button>
        <div class="action-id">${displayActionId(a.id)}</div>
        <div class="action-main">
          <div class="action-desc">${esc(actField(e, a, 'desc'))}${changed?'<span class="changed-dot"></span>':''}${opts.newIds.has(a.id)?'<span class="new-action-tag">new</span>':''}</div>
          <div class="action-meta">
            <span class="badge badge-status-${sk}">${statusLabel(e.status)}</span>
            <span class="badge badge-priority-${pk}">${e.priority}</span>
            <span class="meta-text action-owner">${esc(actField(e, a, 'owner'))}</span>
            <span class="meta-sep">·</span>
            <span class="meta-text">${formatDateDisplay(actField(e, a, 'date'))}</span>
            ${e.due?`<span class="meta-sep">·</span><span class="meta-text${isOverdue?' meta-overdue':''}">Due ${formatDateDisplay(e.due)}${isOverdue?' <span class="overdue-tag">OVERDUE</span>':''}</span>`:''}
            ${e.closedDate && e.status !== 'Cancelled' ? `<span class="meta-sep">·</span><span class="meta-text">Closed ${formatDateDisplay(e.closedDate)}</span>` : ''}
            ${typeof e.progress === 'number' && e.status !== 'Cancelled' ? `<span class="meta-sep">·</span><span class="progress-pill">${e.progress}%</span>` : ''}
            ${isBlocked ? `<span class="blocker-tag" title="Has open blocker(s)">BLOCKED</span>` : ''}
            ${_wbsPillHtml(a.id)}
            ${Array.isArray(e.labels)&&e.labels.length?e.labels.map(l=>`<span class="label-chip-meta">${esc(l)}</span>`).join(''):''}
            ${hasUpdate?`<span class="update-pill"><svg viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="5" cy="5" r="3.5"/><path d="M5 3v2.2l1.2 1.2"/></svg>${e.updates.length} update${e.updates.length>1?'s':''}</span>`:''}
          </div>
        </div>
        <button class="focus-marker${isFocused?' focused':''}" title="${isFocused?'Clear focus':'Mark as focus'}" onclick="toggleFocus(${a.id});event.stopPropagation()">${FOCUS_CROSSHAIR_SVG}</button>
        <div class="action-chevron"><svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M4.5 7L9 11.5L13.5 7"/></svg></div>
        <button class="card-del-btn" title="Delete action" onclick="deleteAction(${a.id});event.stopPropagation()"><svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M4 4l10 10M14 4l-10 10"/></svg></button>
      </div>
      <div class="action-details">
        <div class="detail-grid">
          <div class="detail-item"><div class="detail-label">Status</div>
            <select class="edit-select" onchange="updateField(${a.id},'status',this.value)" onclick="event.stopPropagation()">
              <option value="In progress"${e.status==='In progress'?' selected':''}>In progress</option>
              <option value="Not yet started"${e.status==='Not yet started'?' selected':''}>Not started</option>
              <option value="On hold"${e.status==='On hold'?' selected':''}>On hold</option>
              <option value="Completed"${e.status==='Completed'?' selected':''}>Completed</option>
              <option value="Cancelled"${e.status==='Cancelled'?' selected':''}>Cancelled</option>
            </select></div>
          <div class="detail-item"><div class="detail-label">Priority</div>
            <select class="edit-select" onchange="updateField(${a.id},'priority',this.value)" onclick="event.stopPropagation()">
              <option value="High"${e.priority==='High'?' selected':''}>High</option>
              <option value="Medium"${e.priority==='Medium'?' selected':''}>Medium</option>
              <option value="Low"${e.priority==='Low'?' selected':''}>Low</option>
            </select></div>
          <div class="detail-item"><div class="detail-label">Date raised</div>
            <input type="date" class="edit-date" value="${esc(actField(e, a, 'date') || '')}" onchange="updateField(${a.id},'date',this.value)" onclick="event.stopPropagation()"></div>
          <div class="detail-item"><div class="detail-label">Due date</div>
            <input type="date" class="edit-date" value="${esc(e.due||'')}" onchange="updateField(${a.id},'due',this.value)" onclick="event.stopPropagation()"></div>
          <div class="detail-item"><div class="detail-label">Closed date</div>
            <input type="date" class="edit-date" value="${e.closedDate||''}" onchange="updateField(${a.id},'closedDate',this.value)" onclick="event.stopPropagation()"></div>
          <div class="detail-item"><div class="detail-label">Progress (%)</div>
            <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap">
              <input type="number" min="0" max="100" step="5" class="edit-select progress-input" value="${typeof e.progress==='number'?e.progress:''}" placeholder="—" onchange="updateField(${a.id},'progress',this.value)" onclick="event.stopPropagation()">
              <button class="progress-chip" type="button" onclick="updateField(${a.id},'progress',25);event.stopPropagation()">25</button>
              <button class="progress-chip" type="button" onclick="updateField(${a.id},'progress',50);event.stopPropagation()">50</button>
              <button class="progress-chip" type="button" onclick="updateField(${a.id},'progress',75);event.stopPropagation()">75</button>
              <button class="progress-chip" type="button" onclick="updateField(${a.id},'progress',100);event.stopPropagation()">100</button>
              <!-- v2.90: removed inline progress-bar-mini (the % pill in meta row still shows the value) -->
            </div></div>
          <div class="detail-item"><div class="detail-label">Owner</div>
            <input class="edit-select" type="text" value="${esc(actField(e, a, 'owner'))}" onchange="updateField(${a.id},'owner',this.value)" onclick="event.stopPropagation()" placeholder="e.g. project lead" list="stakeholderList"></div>
          <div class="detail-item"><div class="detail-label">Requested by</div>
            <input class="edit-select" type="text" value="${esc(e.requestor||'')}" onchange="updateField(${a.id},'requestor',this.value)" onclick="event.stopPropagation()" placeholder="e.g. sponsor or stakeholder" list="stakeholderList"></div>
          <div class="detail-item"><div class="detail-label">Source / meeting</div>
            <input class="edit-select" type="text" value="${esc(actField(e, a, 'source'))}" onchange="updateField(${a.id},'source',this.value)" onclick="event.stopPropagation()" placeholder="e.g. Service meeting"></div>
          <div class="detail-item" style="grid-column:1/-1"><div class="detail-label">Labels</div>
            <div class="chips-row" onclick="event.stopPropagation()">
              ${(Array.isArray(e.labels)?e.labels:[]).map((l,i) => `<span class="label-chip">${esc(l)}<span class="chip-x" onclick="removeLabel(${a.id},${i});event.stopPropagation()" title="Remove"><svg viewBox=\"0 0 12 12\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.8\"><path d=\"M2 2l8 8M10 2l-8 8\"/></svg></span></span>`).join('')}
              <input class="label-add-input" type="text" list="labelList" placeholder="Add label…" onkeydown="if(event.key==='Enter'){addLabel(${a.id},this.value);this.value='';event.preventDefault();}" onblur="if(this.value.trim()){addLabel(${a.id},this.value);this.value='';}" onclick="event.stopPropagation()">
            </div>
          </div>
          ${opts.isSubAction
            /* v2.90: sub-action shows Parent action chip in edit view */
            ? `<div class="detail-item" style="grid-column:1/-1"><div class="detail-label">Parent action</div>
                 <div class="parent-picker-row">
                   <span class="parent-chip" onclick="event.stopPropagation()">${esc(chipLabel(e.parentId))}<span class="parent-chip-x" onclick="removeParent(${a.id});event.stopPropagation()" title="Remove parent (un-nest)"><svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M2 2l8 8M10 2l-8 8"/></svg></span></span>
                 </div>
               </div>`
            /* v2.90: top-level action shows Sub-actions section.
               When no subs yet → "+ Add sub-action" button for discoverability.
               When subs exist → info text + Parent picker field for re-parenting THIS top-level action. */
            : `<div class="detail-item" style="grid-column:1/-1"><div class="detail-label">Sub-actions</div>
                 <div class="parent-picker-row">
                   ${hasSubActions(a.id)
                     ? `<span class="meta-text" style="font-size:11px;color:var(--text3)">Managed below (${getSubActionIds(a.id).length})</span>`
                     : `<button class="add-sub-btn" type="button" onclick="addSubActionFromButton(${a.id});event.stopPropagation()"><svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M6 1v10M1 6h10"/></svg>Add sub-action</button>`
                   }
                 </div>
                 <div class="detail-label" style="margin-top:10px">Parent action</div>
                 <div class="parent-picker-row">
                   <input class="parent-chip-input" type="text" placeholder="#?" onkeydown="if(event.key==='Enter'){handleParentInput(${a.id},this);event.preventDefault();}" onclick="event.stopPropagation()" title="Type the number you see on another card (e.g. #5) to nest this under it">
                   <span class="meta-text" style="font-size:11px;color:var(--text3)">Type a card's number (e.g. #5) to nest this action under it</span>
                 </div>
               </div>`
          }
          <div class="detail-item" style="grid-column:1/-1"><div class="detail-label">Title</div>
            <textarea class="edit-textarea desc-field" onchange="updateField(${a.id},'desc',this.value)" oninput="updateDescLive(${a.id},this.value)" onclick="event.stopPropagation()" placeholder="Action title">${esc(actField(e, a, 'desc') || '')}</textarea></div>
          <div class="detail-item" style="grid-column:1/-1"><div class="detail-label">Description</div>
            <textarea class="edit-textarea" onchange="updateField(${a.id},'details',this.value)" onclick="event.stopPropagation()" placeholder="Background, context, technical details…">${esc(e.details||'')}</textarea></div>
        </div>
        <!-- v2.21: dependencies + related-links section -->
        <div class="chips-section" onclick="event.stopPropagation()">
          <div class="chips-label">Blocked by</div>
          <div class="chips-row">
            ${(e.dependsOn || []).map(targetId => {
              const t = edits[targetId];
              const closed = t && isClosedStatus(t.status);
              return `<span class="chip ${closed ? 'chip-blocks' : 'chip-blocked'}" onclick="navigateToAction(${targetId});event.stopPropagation()" title="${closed ? 'Satisfied (closed)' : 'Open blocker'}"><span class="chip-arrow">→</span><span class="chip-text">${esc(chipLabel(targetId))}</span><span class="chip-x" onclick="removeDependency(${a.id},${targetId});event.stopPropagation()" title="Remove"><svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M2 2l8 8M10 2l-8 8"/></svg></span></span>`;
            }).join('')}
            <input class="chip-add-input" type="text" placeholder="Add blocker — e.g. #5" onkeydown="if(event.key==='Enter'){handleDependencyInput(${a.id},'dependsOn',this);event.preventDefault();}" onclick="event.stopPropagation()">
          </div>
          ${(() => {
            const blocks = getDerivedBlockedBy(a.id);
            if (blocks.length === 0) return '';
            return `<div class="chips-label" style="margin-top:10px">Blocks</div>
              <div class="chips-row">${blocks.map(srcId => `<span class="chip chip-blocks" onclick="navigateToAction(${srcId});event.stopPropagation()" title="Read-only — edit from source action"><span class="chip-arrow">←</span><span class="chip-text">${esc(chipLabel(srcId))}</span></span>`).join('')}</div>`;
          })()}
          <div class="chips-label" style="margin-top:10px">Related to</div>
          <div class="chips-row">
            ${(e.relatedTo || []).map(targetId => `<span class="chip chip-related" onclick="navigateToAction(${targetId});event.stopPropagation()" title="Soft link"><span class="chip-text">${esc(chipLabel(targetId))}</span><span class="chip-x" onclick="removeRelated(${a.id},${targetId});event.stopPropagation()" title="Remove"><svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M2 2l8 8M10 2l-8 8"/></svg></span></span>`).join('')}
            <input class="chip-add-input" type="text" placeholder="Add related — e.g. #12" onkeydown="if(event.key==='Enter'){handleDependencyInput(${a.id},'relatedTo',this);event.preventDefault();}" onclick="event.stopPropagation()">
          </div>
          ${(() => {
            const rels = getDerivedRelatedFrom(a.id);
            if (rels.length === 0) return '';
            return `<div class="chips-label" style="margin-top:10px">Related from</div>
              <div class="chips-row">${rels.map(srcId => `<span class="chip chip-related" onclick="navigateToAction(${srcId});event.stopPropagation()" title="Read-only — edit from source action"><span class="chip-text">${esc(chipLabel(srcId))}</span></span>`).join('')}</div>`;
          })()}
        </div>
        <div class="update-section">
          <div class="update-label">Meeting updates <span class="live-tag">live</span></div>
          <div class="update-log" id="log-${a.id}">${updateLog}</div>
          <textarea class="update-textarea" id="ta-${a.id}" placeholder="Add a note, decision, or update from this meeting…" onclick="event.stopPropagation()" onkeydown="handleKey(event,${a.id})"></textarea>
          <div class="update-actions">
            <button class="add-update-btn" onclick="addUpdate(${a.id});event.stopPropagation()">Add update</button>
            <span class="hint-text">or Ctrl+Enter</span>
          </div>
        </div>
      </div>
      ${_renderSubsContainerHTML(a, opts)}
    </div>`;
}

/* v2.90: build the sub-actions container for a parent (or '' if none to show).
   Visibility rule:
     - parent matches filter → ALL its subs visible
     - parent doesn't match but some subs match → only matching subs visible
   The "+ Add sub-action" button always shows when the parent has any subs in data
   (regardless of current filter). For parents with ZERO subs, the button appears
   in the expanded edit view via a separate placement (see step 8e). */
function _renderSubsContainerHTML(a, opts) {
  if (opts.isSubAction) return '';  /* sub-actions don't get their own sub-container (single-level) */
  const allSubIds = getSubActionIds(a.id);
  if (allSubIds.length === 0) return '';
  const parentMatched = opts.matchedIds && opts.matchedIds.has(a.id);
  const visibleSubIds = parentMatched
    ? allSubIds
    : allSubIds.filter(sid => opts.matchedIds && opts.matchedIds.has(sid));
  const subActionsHtml = visibleSubIds.map(sid => {
    const sub = getAllActions().find(x => x.id === sid);
    return sub ? _renderActionCardHTML(sub, { ...opts, isSubAction: true }) : '';
  }).join('');
  return `<div class="subs-container" onclick="event.stopPropagation()">
    ${subActionsHtml ? `<div class="subs-list">${subActionsHtml}</div>` : ''}
    <div class="add-sub-row">
      <button class="add-sub-btn" type="button" onclick="addSubActionFromButton(${a.id});event.stopPropagation()">
        <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M6 1v10M1 6h10"/></svg>
        Add sub-action
      </button>
    </div>
  </div>`;
}

/* ──────────────────────────────────────────────────────────────────────
   v3.0.2 — Search-result match highlighting
   ──────────────────────────────────────────────────────────────────────
   Both Actions and Minutes have a search field today. Each filters cards
   by substring match. v3.0.2 adds visual highlighting of the matched
   substring inside every visible card, mirroring the filter's exact
   matching logic.

   Behavior:
     - Case-insensitive substring of the full search string (multi-word
       phrases highlight as one contiguous span, not per-word — same as
       what the filter does).
     - Live on every keystroke (no debounce; user explicit choice).
     - Text-node walking with span injection — never naive innerHTML
       replace, so `<`, `>`, `&` in search strings are safe.
     - Targets visible display divs only. Inputs/textareas/selects
       cannot host inline HTML, so highlight skips them. Filter still
       hides non-matching cards, so the user is never lost.
     - The Actions tab gets highlight on: title (.action-desc), owner
       (.action-owner), labels (.label-chip, .label-chip-meta),
       and past update entries (.update-entry-text).
     - The Minutes tab gets highlight on: session subject
       (.session-title-display) and attendee preview
       (.session-attendee-preview). Body and action-summary live inside
       <textarea> elements which cannot be highlighted inline; filter
       still catches body/summary matches at the card level.
*/

/* Strip any existing search-highlight spans inside a root element.
   Used at the start of every render so we don't double-wrap. */
function _clearSearchHighlights(rootEl) {
  if (!rootEl) return;
  const highlights = rootEl.querySelectorAll('.search-highlight');
  highlights.forEach(span => {
    const parent = span.parentNode;
    if (!parent) return;
    while (span.firstChild) parent.insertBefore(span.firstChild, span);
    parent.removeChild(span);
    parent.normalize();
  });
}

/* Walk text nodes within `el`, wrap occurrences of `qLower` (case-insensitive
   substring) in <span class="search-highlight">. */
function _walkAndHighlight(el, qLower) {
  if (!el || !qLower) return;
  /* Determine the "meaningfully focused" element: a contenteditable, input,
     or textarea that has the focus. Body or null means nothing is focused,
     and we should NOT treat that as a reason to skip text. */
  const ae = document.activeElement;
  const focusedEditable = (ae && ae !== document.body && ae !== document.documentElement &&
    (ae.isContentEditable || ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA'))
    ? ae : null;

  /* If the entire target is the focused editable, skip — interferes with typing.
     The renderers strip + re-apply on blur. */
  if (focusedEditable && (focusedEditable === el || focusedEditable.contains(el))) return;

  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
    acceptNode: function(node) {
      if (!node.nodeValue) return NodeFilter.FILTER_REJECT;
      /* Skip text inside an existing highlight span (defensive — should be
         stripped before this call). */
      if (node.parentNode && node.parentNode.classList &&
          node.parentNode.classList.contains('search-highlight')) {
        return NodeFilter.FILTER_REJECT;
      }
      /* Skip text inside the focused editable subtree, if any. */
      if (focusedEditable && node.parentNode &&
          (node.parentNode === focusedEditable || focusedEditable.contains(node.parentNode))) {
        return NodeFilter.FILTER_REJECT;
      }
      return node.nodeValue.toLowerCase().includes(qLower)
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT;
    }
  });
  const matches = [];
  let n; while ((n = walker.nextNode())) matches.push(n);
  matches.forEach(textNode => {
    const text = textNode.nodeValue;
    const lower = text.toLowerCase();
    const frag = document.createDocumentFragment();
    let i = 0;
    while (i < text.length) {
      const found = lower.indexOf(qLower, i);
      if (found === -1) {
        frag.appendChild(document.createTextNode(text.slice(i)));
        break;
      }
      if (found > i) frag.appendChild(document.createTextNode(text.slice(i, found)));
      const span = document.createElement('span');
      span.className = 'search-highlight';
      span.textContent = text.slice(found, found + qLower.length);
      frag.appendChild(span);
      i = found + qLower.length;
    }
    if (textNode.parentNode) textNode.parentNode.replaceChild(frag, textNode);
  });
}

/* Apply highlights to a list of selector-targeted elements inside `rootEl`. */
function highlightSearchMatches(rootEl, query, selectors) {
  _clearSearchHighlights(rootEl);
  if (!rootEl || !query) return;
  const q = String(query).trim().toLowerCase();
  if (!q) return;
  selectors.forEach(sel => {
    rootEl.querySelectorAll(sel).forEach(el => _walkAndHighlight(el, q));
  });
}

/* Highlight selectors per tab. Tuned to (a) what the filter actually
   searches against and (b) what's visible in non-form-control display
   elements. */
const _ACTIONS_HIGHLIGHT_SELECTORS = [
  '.action-desc',         // title / description (always visible)
  '.action-owner',        // owner span in meta row (always visible)
  '.label-chip-meta',     // collapsed-view label pills
  '.label-chip',          // expanded-view label pills (editable, with × child)
  '.update-entry-text',   // past update entries (expanded view)
];
const _MINUTES_HIGHLIGHT_SELECTORS = [
  '.session-title-display',     // subject when collapsed
  '.session-attendee-preview',  // attendees preview when collapsed
];


function render() {
  captureUpdateDrafts();  /* v2.18.1: snapshot any in-progress drafts before the DOM is wiped */
  /* v2.90: hierarchy-aware filtering.
     getFiltered returns all matching actions (top-level + sub-actions).
     For rendering, top-level cards render as outer elements; sub-actions render nested.
     A top-level action is shown if it itself matches OR any of its sub-actions matches. */
  const _filteredAll = getFiltered();
  const matchedIds = new Set(_filteredAll.map(a => a.id));
  const topLevelToShow = getAllActions().filter(a => {
    const e = edits[a.id];
    if (e.parentId != null) return false;  /* sub-actions handled in nested render */
    if (matchedIds.has(a.id)) return true;
    return getSubActionIds(a.id).some(sid => matchedIds.has(sid));
  });
  let filtered = topLevelToShow, sorted = getSorted(topLevelToShow);
  /* v2.22: three-tier sort. Within sorted result:
     - Flagged cards (on the agenda) float to top, sorted among themselves by active sort.
     - Focus crosshair always wins ABSOLUTE pos 0 above all flagged.
     Hide-closed has already filtered out closed cards via getFiltered. */
  /* v2.90: family-aware flagged check — a parent floats if it OR any of its subs is flagged */
  const _isFamilyFlagged = (a) => {
    if (edits[a.id] && edits[a.id].flagged) return true;
    return getSubActionIds(a.id).some(sid => edits[sid] && edits[sid].flagged);
  };
  const flagged = sorted.filter(_isFamilyFlagged);
  const unflagged = sorted.filter(a => !_isFamilyFlagged(a));
  sorted = [...flagged, ...unflagged];
  /* v2.19 item 8: float the focused action to position 0, regardless of sort.
     Only applies if the focused action survived filtering — otherwise nothing
     to float (it would be hidden anyway). */
  if (appMeta.focusActionId != null) {
    const fIdx = sorted.findIndex(a => a.id === appMeta.focusActionId);
    if (fIdx > 0) {
      const [f] = sorted.splice(fIdx, 1);
      sorted.unshift(f);
    }
  }
  const list=document.getElementById('actionsList'), empty=document.getElementById('emptyState');
  if(!sorted.length){
    list.innerHTML='';
    /* v2.90: populate active-filter pills + show polished empty state */
    const pillsEl = document.getElementById('emptyActiveFilters');
    if (pillsEl) pillsEl.innerHTML = _buildActiveFilterPills();
    empty.style.display = (actionsView === 'board') ? 'none' : 'block';
    return;
  }
  empty.style.display='none';
  /* v2.96: removed the "N on the agenda" count pill above the list — the
     pill strip's AGENDA chip already shows the count, and individual cards
     show the filled bookmark icon. The legacy banner became redundant. */
  const expandedIds=new Set([...document.querySelectorAll('.action-card.expanded')].map(el=>Number(el.dataset.id)));
  const newIds=new Set(newActions.map(a=>a.id));

  list.innerHTML = sorted.map(a => _renderActionCardHTML(a, { isSubAction: false, expandedIds, newIds, matchedIds })).join('');
  restoreUpdateDrafts();  /* v2.18.1: put back any drafts that were captured before the wipe */
  /* v3.0.2: apply search-result highlighting to the freshly-rendered cards */
  highlightSearchMatches(list, searchQuery, _ACTIONS_HIGHLIGHT_SELECTORS);
}

/* ── v4.0: Kanban board (Actions tab) ─────────────────────────────────────
   List <-> Board toggle. The board groups the SAME filtered actions as the
   list (search + priority + agenda/overdue/blocked) into status columns; the
   columns own the status axis, so activeStatus + hideCompleted are neutralised
   here (and the status-filter row + Hide-closed hide in board view). A drop
   calls updateField(id,'status',…) so the app's status logic (closedDate,
   Completed→progress 100, diff, persistence) is reused. Sub-actions render as
   the parent card with an expandable sub-count. View is session-only. */
const KANBAN_COLUMNS = [
  { status:'Not yet started', dot:'var(--col-open)',   terminal:false },
  { status:'In progress',     dot:'var(--col-prog)',   terminal:false },
  { status:'On hold',         dot:'var(--col-hold)',   terminal:false },
  { status:'Completed',       dot:'var(--col-done)',   terminal:true  },
  { status:'Cancelled',       dot:'var(--col-cancel)', terminal:true  },
];
const kbCollapsed = { 'Completed': false, 'Cancelled': false };
const kbOpenSubs = {};

function setActionsView(v) {
  actionsView = (v === 'board') ? 'board' : 'list';
  const vb = document.getElementById('vtBoard'), vl = document.getElementById('vtList');
  _setSelected(vb, actionsView === 'board');   /* v13.1 (A58) */
  _setSelected(vl, actionsView === 'list');
  document.body.classList.toggle('actions-board', actionsView === 'board');
  const board = document.getElementById('actionBoard');
  if (board) board.style.display = (actionsView === 'board') ? 'flex' : 'none';
  const list = document.getElementById('actionsList');
  if (list) list.style.display = (actionsView === 'board') ? 'none' : '';
  if (actionsView === 'board') renderActionBoard();
  /* ⚠ v15.2: switching to LIST repaints too. v14.2 (d) fixed the blank-landing for the
     board-CLICK jump only; the PLAIN toggle kept the hole — with the board up, a no-match
     search wrote an inline display:none onto the empty-state, and toggling to List then
     showed an empty container with the banner still suppressed: a completely blank pane
     with no cards and no explanation. render() recomputes both the cards and the banner. */
  else if (typeof render === 'function') render();
}

/* Board-scoped filtered set: reuse getFiltered() but neutralise the per-status
   filter + hide-closed (the columns provide the status axis). */
function getBoardFiltered() {
  const _s = activeStatus, _h = hideCompleted;
  activeStatus = 'all'; hideCompleted = false;
  try { return getFiltered(); } finally { activeStatus = _s; hideCompleted = _h; }
}
/* Top-level cards for the board (a parent shows if it OR any sub matches). */
function getBoardTopLevel() {
  const matchedIds = new Set(getBoardFiltered().map(a => a.id));
  return getAllActions().filter(a => {
    const e = edits[a.id];
    if (!e || e.parentId != null) return false;
    if (matchedIds.has(a.id)) return true;
    return getSubActionIds(a.id).some(sid => matchedIds.has(sid));
  });
}
function _kbInitials(name) { const p = String(name || '').trim().split(/\s+/).filter(Boolean); return p.length ? p.map(w => w[0]).join('').slice(0, 2).toUpperCase() : '—'; }
function _kbEsc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;' }[c])); }

function buildKanbanCard(a) {
  const e = edits[a.id];
  const status = e.status || STATUS_DISPLAY[a.status] || a.status;
  const terminal = isClosedStatus(status);
  const pri = (e.priority || 'Medium').toLowerCase();
  const desc = actField(e, a, 'desc') || '';
  const owner = actField(e, a, 'owner') || '';
  const labels = Array.isArray(e.labels) ? e.labels : [];
  const due = e.due || '';
  const todayStr = _todayISO();
  const overdue = due && !terminal && due < todayStr;
  const prog = (typeof e.progress === 'number') ? e.progress : null;
  /* v13.1 (A52): through the shared definition, so the board and the card cannot diverge */
  const openBlockers = (Array.isArray(e.dependsOn) ? e.dependsOn : []).filter(_blockerStillBlocks);
  const subIds = getSubActionIds(a.id);
  const subsOpen = !!kbOpenSubs[a.id];
  const flagSvg = '<svg viewBox="0 0 14 14" fill="currentColor"><path d="M3 1v12M3 2h7l-1.5 2.2L10 6.4H3"/></svg>';
  const chev = '<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M4 2l4 4-4 4"/></svg>';
  return `<div class="kb-card${terminal ? ' terminal-card' : ''}" data-id="${a.id}">
    <div class="kb-card-top">
      <span class="kb-pri ${pri}">${_kbEsc(pri)}</span>
      <span class="kb-top-spacer"></span>
      ${e.flagged ? `<span class="kb-flag" title="On the agenda">${flagSvg}</span>` : ''}
      <span class="kb-id">${displayActionId(a.id)}</span>
    </div>
    <div class="kb-title">${_kbEsc(desc)}</div>
    ${(labels.length || openBlockers.length) ? `<div class="kb-labels">
      ${labels.map(l => `<span class="kb-label">${_kbEsc(l)}</span>`).join('')}
      ${openBlockers.length ? `<span class="kb-blocked">blocked by ${openBlockers.map(displayActionId).join(', ')}</span>` : ''}
    </div>` : ''}
    ${prog != null ? `<div class="kb-progress"><div class="kb-progress-bar" style="width:${Math.max(0, Math.min(100, prog))}%"></div></div>` : ''}
    <div class="kb-meta">
      <span class="kb-owner">${owner ? `<span class="kb-avatar">${_kbEsc(_kbInitials(owner))}</span><span class="kb-owner-name">${_kbEsc(owner)}</span>` : '<span class="kb-owner-name" style="color:var(--text3)">No owner</span>'}</span>
      <span class="kb-meta-spacer"></span>
      <span class="kb-due${overdue ? ' overdue' : ''}">${due ? _kbEsc(due) : 'no date'}</span>
    </div>
    ${subIds.length ? `<div class="kb-subs${subsOpen ? ' open' : ''}" data-subs="${a.id}">
      <button class="kb-subs-toggle" data-subtoggle="${a.id}">${chev}${subIds.length} sub-action${subIds.length > 1 ? 's' : ''}</button>
      <div class="kb-sublist">
        ${subIds.map(sid => { const se = edits[sid] || {}; const sa = getAllActions().find(x => x.id === sid) || {}; const sdone = isClosedStatus(se.status); return `<div class="kb-subitem${sdone ? ' done' : ''}"><span class="kb-subdot"></span>${_kbEsc(actField(se, sa, 'desc') || displayActionId(sid))}</div>`; }).join('')}
      </div>
    </div>` : ''}
  </div>`;
}

/* ⚠ v14.2 (d): OWNER COLUMNS ARE COMPUTED FROM THE VISIBLE CARDS, not from a list anywhere.
   Owners are free text, so "Bob" and "bob" would split into two columns: they are grouped
   case-insensitively and shown in the MAJORITY spelling, with everything unowned in a single
   Unassigned column at the end. Every card lands in exactly one column, which is what makes
   owner a legitimate grouping where labels (multi-valued) would not be.
   ⚠ Status columns are always the same five; owner columns are however many people there are,
   and the board is a flex row, so a large team scrolls sideways. That is deliberate - capping
   or bucketing would hide someone's work. */
function _boardColumns(top) {
  if (groupBy !== 'owner') return KANBAN_COLUMNS.map(c => Object.assign({ key: c.status }, c));
  const seen = new Map();          /* lowercased -> { spellings: Map, n } */
  top.forEach(a => {
    const raw = (actField(edits[a.id] || {}, a, 'owner') || '').trim();
    if (!raw) return;
    const k = raw.toLowerCase();
    if (!seen.has(k)) seen.set(k, new Map());
    const sp = seen.get(k);
    sp.set(raw, (sp.get(raw) || 0) + 1);
  });
  const cols = [...seen.entries()].map(([k, sp]) => {
    let best = '', n = -1;
    sp.forEach((count, spelling) => { if (count > n) { n = count; best = spelling; } });
    return { key: k, status: best, dot: 'var(--col-prog)', terminal: false, owner: true };
  }).sort((a, b) => a.status.localeCompare(b.status));
  cols.push({ key: '\u0000unassigned', status: 'Unassigned', dot: 'var(--col-open)',
              terminal: false, owner: true, unassigned: true });
  return cols;
}

function renderActionBoard() {
  const board = document.getElementById('actionBoard');
  if (!board) return;
  const top = getBoardTopLevel();
  board.innerHTML = _boardColumns(top).map(col => {
    /* ⚠ v14.2 (d): getSorted() is what the LIST uses. The board filled its columns in raw
       array order and never called it, so the sort control was inert here. */
    const inCol = col.owner
      ? top.filter(a => {
          const o = (actField(edits[a.id] || {}, a, 'owner') || '').trim();
          return col.unassigned ? !o : o.toLowerCase() === col.key;
        })
      : top.filter(a => (edits[a.id] && edits[a.id].status) === col.status);
    const cards = getSorted(inCol);
    const collapsed = col.terminal && kbCollapsed[col.status];
    return `<div class="kb-col${col.terminal ? ' terminal' : ''}${collapsed ? ' collapsed' : ''}" data-status="${_kbEsc(col.status)}">
      <div class="kb-col-head">
        <span class="kb-col-dot" style="background:${col.dot}"></span>
        <span class="kb-col-name">${col.status}</span>
        <span class="kb-col-count">${cards.length}</span>
        <span class="kb-col-spacer"></span>
        ${col.terminal ? `<button class="kb-collapse" data-collapse="${col.status}" title="Collapse / expand"><svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M3 4.5l3 3 3-3"/></svg></button>` : ''}
      </div>
      <div class="kb-cards">
        ${cards.length ? cards.map(buildKanbanCard).join('') : '<div class="kb-empty">—</div>'}
      </div>
    </div>`;
  }).join('');
}

/* delegated: collapse terminal column + toggle a card's sub-list */
(function _kbDelegate() {
  const board = document.getElementById('actionBoard');
  if (!board) return;
  board.addEventListener('click', (e) => {
    const col = e.target.closest('[data-collapse]');
    if (col) { const s = col.dataset.collapse; kbCollapsed[s] = !kbCollapsed[s]; renderActionBoard(); return; }
    const st = e.target.closest('[data-subtoggle]');
    if (st) { const id = +st.dataset.subtoggle; kbOpenSubs[id] = !kbOpenSubs[id]; renderActionBoard(); return; }
  });
})();

/* pointer drag-to-set-status */
let _kbDrag = null;
const KB_DRAG_THRESHOLD = 5;   /* px - below this the gesture is a CLICK, not a drag */
function _kbMove(e) {
  if (!_kbDrag) return;
  if (_kbDrag.armed) {
    if (!_kbDrag.canDrag) return;                       /* owner mode: never becomes a drag */
    const dx = e.clientX - _kbDrag.x0, dy = e.clientY - _kbDrag.y0;
    if ((dx * dx + dy * dy) < (KB_DRAG_THRESHOLD * KB_DRAG_THRESHOLD)) return;
    /* past the threshold: NOW it is a drag, and only now is the ghost built */
    const clone = _kbDrag.card.cloneNode(true);
    clone.classList.add('kb-card-floating');
    clone.style.width = _kbDrag.rect.width + 'px';
    clone.style.left = _kbDrag.rect.left + 'px';
    clone.style.top = _kbDrag.rect.top + 'px';
    document.body.appendChild(clone);
    _kbDrag.card.classList.add('ghost');
    _kbDrag.clone = clone;
    _kbDrag.armed = false;
  }
  _kbDrag.clone.style.left = (e.clientX - _kbDrag.dx) + 'px';
  _kbDrag.clone.style.top = (e.clientY - _kbDrag.dy) + 'px';
  const el = document.elementFromPoint(e.clientX, e.clientY);
  const col = el && el.closest('.kb-col');
  document.querySelectorAll('.kb-col').forEach(c => c.classList.toggle('drop-target', c === col));
  _kbDrag.target = col ? col.dataset.status : null;
}
function _kbUp() {
  if (!_kbDrag) return;
  const d = _kbDrag; _kbDrag = null;
  document.querySelectorAll('.kb-col').forEach(c => c.classList.remove('drop-target'));
  if (d.clone) d.clone.remove();
  document.removeEventListener('pointermove', _kbMove);
  document.removeEventListener('pointerup', _kbUp);
  /* v14.2 (d): never passed the threshold, so this was a CLICK - jump to it in the list */
  if (d.armed) { d.card.classList.remove('ghost'); jumpToActionFromBoard(d.id); return; }
  if (d.target && d.target !== d.from) {
    if (kbCollapsed[d.target]) kbCollapsed[d.target] = false;   // auto-expand the column you drop into
    updateField(d.id, 'status', d.target);   // reuses closedDate + Completed→100 + diff + persistence + refreshAll
    requestAnimationFrame(() => {
      const moved = document.querySelector('#actionBoard .kb-card[data-id="' + d.id + '"]');
      if (moved) { moved.classList.add('just-moved'); setTimeout(() => moved.classList.remove('just-moved'), 600); }
    });
  } else if (d.card) {
    d.card.classList.remove('ghost');
  }
}
(function _kbDragInit() {
  const board = document.getElementById('actionBoard');
  if (!board) return;
  board.addEventListener('pointerdown', (e) => {
    if (e.button !== 0) return;
    if (e.target.closest('button, input, a, select, textarea, .kb-subs')) return;
    const card = e.target.closest('.kb-card');
    if (!card) return;
    e.preventDefault();
    const id = +card.dataset.id;
    const a = getAllActions().find(x => x.id === id);
    if (!a || !edits[id]) return;
    const rect = card.getBoundingClientRect();
    /* ⚠ v14.2 (d): ARMED, NOT DRAGGING. Pointerdown used to claim the gesture instantly, which
       left no way to express a CLICK - and a plain click on a board card did nothing at all,
       so nothing is displaced by giving it a meaning. The clone is not built until the pointer
       has actually moved past the threshold.
       ⚠ DRAG IS A STATUS-MODE GESTURE ONLY. Under owner grouping a drop would REASSIGN the
       action, and updateField(id,'owner',...) is written from exactly ONE place in the whole
       shell - the text input on the expanded card - so letting a drag do it would invent the
       first other way to change an owner. His own reasoning, verified against the code. */
    _kbDrag = { id, card, clone: null, rect: rect,
                dx: e.clientX - rect.left, dy: e.clientY - rect.top,
                from: edits[id].status, target: null,
                x0: e.clientX, y0: e.clientY, armed: true,
                canDrag: (groupBy === 'status') };
    document.addEventListener('pointermove', _kbMove);
    document.addEventListener('pointerup', _kbUp);
  });
})();

function toggleCard(id) { document.getElementById('card-'+id).classList.toggle('expanded'); syncExpandToggleLabels(); }
/* v2.20: status → closedDate auto-derivation rules.
   open = Not yet started, In progress, On hold
   closed = Completed, Cancelled
   - open → closed: set closedDate to today, ONLY if currently empty
     (preserves original close date across Completed ↔ Cancelled toggles)
   - closed → open (reopen): clear closedDate to '' */
const CLOSED_STATUSES = new Set(['Completed', 'Cancelled']);
function isClosedStatus(s) { return CLOSED_STATUSES.has(s); }
function updateField(id,field,value) {
  /* v2.20: status change auto-derives closedDate */
  if (field === 'status') {
    const prevStatus = edits[id].status;
    const prevClosed = isClosedStatus(prevStatus);
    const nextClosed = isClosedStatus(value);
    if (!prevClosed && nextClosed && !edits[id].closedDate) {
      edits[id].closedDate = todayISO();  // first open→closed transition
    } else if (prevClosed && !nextClosed) {
      edits[id].closedDate = '';  // reopen — clear the close date
    }
    // closed → closed: preserve existing closedDate (no-op)
    /* v2.22: status → progress auto-derivation.
       - any → Completed: set progress = 100
       - Completed → other: keep progress at 100 (sticky)
       - Cancelled and other states: do not touch progress */
    if (value === 'Completed') {
      edits[id].progress = 100;
    }
  }
  /* v2.22: clamp manual progress edits to 0..100 (or null for empty) */
  if (field === 'progress') {
    if (value === '' || value === null || value === undefined) {
      value = null;
    } else {
      const n = Number(value);
      if (Number.isNaN(n)) value = null;
      else value = Math.max(0, Math.min(100, Math.round(n)));
    }
  }
  edits[id][field]=value;
  sessionEditedActionIds.add(id);
  markDirty();
  refreshAll();
  document.getElementById('card-'+id)&&document.getElementById('card-'+id).classList.add('expanded');
  if (field==='labels') updateLabelDatalist();
}
function updateDescLive(id, value) {
  /* Live-update the collapsed desc line as user types — avoids losing focus mid-keystroke */
  const card = document.getElementById('card-'+id);
  if (!card) return;
  const descEl = card.querySelector('.action-desc');
  if (!descEl) return;
  const dotAndTag = descEl.innerHTML.match(/(<span class="changed-dot".*?$)/);
  descEl.textContent = value;
  if (dotAndTag) descEl.insertAdjacentHTML('beforeend', dotAndTag[1]);
  edits[id].desc = value;
  sessionEditedActionIds.add(id);
  markDirty();
}
/* v2.11: stable note IDs so we can track add/remove deltas across sessions */
function makeNoteId(existingUpdates) {
  const taken = new Set((Array.isArray(existingUpdates) ? existingUpdates : [])
    .map(u => u && u.noteId).filter(Boolean));
  const base = 'n_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
  return _claimUniqueNoteId(base, taken);
}
function addUpdate(id) {
  const ta=document.getElementById('ta-'+id);
  const text=(ta?ta.value:'').trim();
  if(!text) return;
  edits[id].updates.push({noteId:makeNoteId(edits[id].updates),time:nowStamp(),text});
  delete pendingUpdates[id];  /* v2.18.1: committed → no longer a draft */
  if (ta) ta.value = '';     /* v2.18.1: clear the source textarea BEFORE refreshAll, so the
                                   capture-on-render hook doesn\'t see this text and put it back. */
  sessionEditedActionIds.add(id);
  markDirty();
  refreshAll();
  document.getElementById('card-'+id)&&document.getElementById('card-'+id).classList.add('expanded');
  const newTa=document.getElementById('ta-'+id);
  if(newTa) newTa.focus();
}
/* v10.3 (W4): guarded — this was the one remaining unguarded splice in the note path
   (its sibling removeLabel has always bounds-checked). A missing edits record or a stale
   index threw instead of no-op'ing. */
function deleteUpdate(id,index) {
  const e = edits[id];
  if (!e || !Array.isArray(e.updates) || index < 0 || index >= e.updates.length) return;
  e.updates.splice(index,1); markDirty(); refreshAll();
  document.getElementById('card-'+id)&&document.getElementById('card-'+id).classList.add('expanded');
}

/* v2.18.1: edit an existing update entry in place. Date/timestamp preserved;
   only the text is mutable. Save on blur, Escape cancels, empty edit reverts
   to original (use the delete X to remove an entry — empty edit is treated as
   "I didn\'t actually mean to clear this"). Only one edit textarea active
   at any time — clicking another entry will commit/cancel the first via blur. */
function editUpdate(id, index) {
  const entry = edits[id] && edits[id].updates && edits[id].updates[index];
  if (!entry) return;
  // Find the text element. Use the log container to scope the lookup so we
  // don\'t accidentally match an entry from a different card.
  const log = document.getElementById('log-' + id);
  if (!log) return;
  const textEl = log.querySelectorAll('.update-entry-text')[index];
  if (!textEl) return;
  // Don\'t double-edit if textarea is already showing
  if (textEl.style.display === 'none') return;

  const original = entry.text;
  const ta = document.createElement('textarea');
  ta.className = 'update-entry-text-edit';
  ta.value = original;
  // Auto-size roughly to content (rows = lines, min 1)
  ta.rows = Math.max(1, original.split('\n').length);

  let finished = false;
  const cleanup = () => {
    if (ta.parentNode) ta.parentNode.removeChild(ta);
    textEl.style.display = '';
  };
  const commit = () => {
    if (finished) return;
    finished = true;
    const newText = ta.value.trim();
    // Empty edit reverts to original (use X to delete)
    if (newText === '' || newText === original) {
      cleanup();
      return;
    }
    entry.text = ta.value;  /* preserve user\'s internal whitespace including newlines */
    markDirty();
    refreshAll();
    const card = document.getElementById('card-' + id);
    if (card) card.classList.add('expanded');
  };
  const cancel = () => {
    if (finished) return;
    finished = true;
    cleanup();
  };

  _onBlurCommit(ta, commit);
  ta.addEventListener('keydown', e => {
    if (e.key === 'Escape') { e.preventDefault(); cancel(); }
    // Ctrl+Enter commits, matching addUpdate convention
    else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); ta.blur(); }
  });
  // Hide the static text element, insert textarea in its place
  textEl.style.display = 'none';
  textEl.parentNode.insertBefore(ta, textEl);
  ta.focus();
  // Place cursor at end of text
  ta.setSelectionRange(ta.value.length, ta.value.length);
}
function handleKey(e,id) { if(e.key==='Enter'&&(e.ctrlKey||e.metaKey)){e.preventDefault();addUpdate(id);} }
function deleteAction(id) {
  const action = [...newActions, ...ACTIONS].find(a => a.id === id);
  if (!action) return;
  /* v2.90: cascade delete — if this is a parent with sub-actions, deletion will remove the whole family. */
  const subIds = getSubActionIds(id);
  const isCascade = subIds.length > 0;
  const familyIds = [id, ...subIds];  /* parent first, then subs in ID order */
  const snippet = action.desc.length > 90 ? action.desc.slice(0, 90) + '…' : action.desc;
  const subList = isCascade
    ? `<br><br><span style="color:#7c3aed;font-weight:500">Also deletes ${subIds.length} sub-action${subIds.length === 1 ? '' : 's'}: ${subIds.map(displayActionId).join(', ')}</span>`
    : '';
  showConfirm(
    'Delete action ' + displayActionId(id) + (isCascade ? ' and its sub-actions?' : '?'),
    'This action will be removed:<br><br><em>"' + esc(snippet) + '"</em>' + subList,
    () => {
      /* v2.22: capture pre-delete state for undo restoration.
         v2.90: extend to family — capture state for parent AND all sub-actions for atomic undo. */
      const savedFamily = [];  /* [{id, savedAction, savedEdits, wasNewActionIdx}, ...] */
      for (const fid of familyIds) {
        const wasNewActionIdx = newActions.findIndex(a => a.id === fid);
        const savedAction = (wasNewActionIdx !== -1)
          ? newActions[wasNewActionIdx]
          : ACTIONS.find(a => a.id === fid);
        if (!savedAction) continue;  /* defensive — shouldn't happen */
        const savedEdits = JSON.parse(JSON.stringify(edits[fid] || null));
        savedFamily.push({ id: fid, savedAction, savedEdits, wasNewActionIdx });
      }
      /* Capture which other actions had references to ANY family member (for undo restoration) */
      const sweptDependsOn = [];  /* [{otherId, targetId}] */
      const sweptRelatedTo = [];
      const familySet = new Set(familyIds);
      Object.keys(edits).forEach(otherIdStr => {
        const oid = Number(otherIdStr);
        if (familySet.has(oid)) return;
        const oe = edits[otherIdStr];
        if (Array.isArray(oe.dependsOn)) {
          oe.dependsOn.forEach(targetId => {
            if (familySet.has(targetId)) sweptDependsOn.push({ otherId: oid, targetId });
          });
        }
        if (Array.isArray(oe.relatedTo)) {
          oe.relatedTo.forEach(targetId => {
            if (familySet.has(targetId)) sweptRelatedTo.push({ otherId: oid, targetId });
          });
        }
      });

      /* Perform the deletion for each family member */
      for (const fid of familyIds) {
        const newIdx = newActions.findIndex(a => a.id === fid);
        if (newIdx !== -1) {
          newActions.splice(newIdx, 1);
          delete edits[fid];
        } else if (ACTIONS.find(a => a.id === fid)) {
          deletedIds.add(fid);
          delete edits[fid];
        }
      }
      /* v2.21: clean up references to ALL family ids from other actions' dependsOn / relatedTo. */
      Object.keys(edits).forEach(otherIdStr => {
        const oe = edits[otherIdStr];
        if (Array.isArray(oe.dependsOn)) {
          oe.dependsOn = oe.dependsOn.filter(d => !familySet.has(d));
        }
        if (Array.isArray(oe.relatedTo)) {
          oe.relatedTo = oe.relatedTo.filter(r => !familySet.has(r));
        }
      });
      /* v10.3 (W3): capture the WBS membership BEFORE refreshAll() — renderWorkstreams
         runs pruneProjectActionLinks, which strips the deleted ids out of every
         workstream's actionIds. The undo restored the action, its edits and its
         dependency references, but NOT this, so an undone delete silently came back
         unassigned and showed up as uncovered work in the coverage strip. */
      const sweptWbs = [];   /* [{wsId, actionId}] */
      WORKSTREAMS.forEach(ws => {
        (Array.isArray(ws.actionIds) ? ws.actionIds : []).forEach(aid => {
          if (familySet.has(aid)) sweptWbs.push({ wsId: ws.id, actionId: aid });
        });
      });
      markDirty();
      refreshAll();

      /* Undo: restore everything — family members and sweep references. */
      const restoreFn = () => {
        for (const fam of savedFamily) {
          if (fam.wasNewActionIdx !== -1) {
            newActions.splice(fam.wasNewActionIdx, 0, fam.savedAction);
          } else {
            deletedIds.delete(fam.id);
          }
          if (fam.savedEdits) edits[fam.id] = fam.savedEdits;
        }
        sweptWbs.forEach(({ wsId, actionId }) => {   /* v10.3 (W3): put the WBS link back */
          const ws = (typeof _wsById === 'function') ? _wsById(wsId) : WORKSTREAMS.find(w => w.id === wsId);
          if (ws && Array.isArray(ws.actionIds) && !ws.actionIds.includes(actionId)) ws.actionIds.push(actionId);
        });
        sweptDependsOn.forEach(({ otherId, targetId }) => {
          if (edits[otherId] && Array.isArray(edits[otherId].dependsOn) && !edits[otherId].dependsOn.includes(targetId)) {
            edits[otherId].dependsOn.push(targetId);
          }
        });
        sweptRelatedTo.forEach(({ otherId, targetId }) => {
          if (edits[otherId] && Array.isArray(edits[otherId].relatedTo) && !edits[otherId].relatedTo.includes(targetId)) {
            edits[otherId].relatedTo.push(targetId);
          }
        });
        markDirty();
        refreshAll();
        showToast(isCascade
          ? 'Action #' + id + ' and ' + subIds.length + ' sub-action' + (subIds.length === 1 ? '' : 's') + ' restored'
          : 'Action #' + id + ' restored',
          { type: 'info' });
      };
      /* Toast message reflects cascade if applicable */
      const descShort = action.desc.length > 50 ? action.desc.slice(0, 47) + '…' : action.desc;
      /* v15.2: the derived number — the confirm dialog beside these already used it, and the
         raw block id ("Deleted #927191000001") disagreed with every card on screen. */
      const toastMsg = isCascade
        ? 'Deleted ' + displayActionId(id) + ' + ' + subIds.length + ' sub-action' + (subIds.length === 1 ? '' : 's')
        : 'Deleted ' + displayActionId(id) + (descShort ? ': ' + descShort : '');
      showUndoToast(toastMsg, restoreFn);
    }
  );
}


/* ── SNAPSHOT + DIFF (Option 2) ── */

function takeSnapshot() {
  const snap = {};
  getAllActions().forEach(a => {
    const e = edits[a.id];
    snap[a.id] = {
      status: e.status,
      priority: e.priority,
      due: e.due || '',
      owner: actField(e, a, 'owner'),
      desc: actField(e, a, 'desc'),
      source: actField(e, a, 'source'),
      date: actField(e, a, 'date'),
      details: e.details || '',
      labels: Array.isArray(e.labels) ? [...e.labels] : [],
      requestor: e.requestor || '',
      /* v3.2: six previously-untracked fields now captured so the meeting
         diff covers the full action shape. Output is consumed by the AI
         consolidation chat, so keep these as raw, structured values. */
      closedDate: e.closedDate || '',
      dependsOn: Array.isArray(e.dependsOn) ? [...e.dependsOn] : [],
      relatedTo: Array.isArray(e.relatedTo) ? [...e.relatedTo] : [],
      progress: (typeof e.progress === 'number') ? e.progress : null,
      flagged: !!e.flagged,
      parentId: (typeof e.parentId === 'number') ? e.parentId : null,
      /* v2.11: store actual updates array (with noteIds) so diff can detect
         which specific notes were added or removed since snapshot. */
      updates: (e.updates || []).map(u => ({ noteId: u.noteId, time: u.time, text: u.text })),
      updateCount: e.updates.length
    };
  });
  return snap;
}

function computeDiff(snapshot, current) {
  current = current || takeSnapshot();
  const changes = [];
  Object.keys(current).forEach(id => {
    const now = current[id], before = snapshot[id];
    if (!before) {
      changes.push({ id: Number(id), desc: now.desc, type: 'added', fields: [] });
      return;
    }
    const fields = [];
    if (now.status !== before.status) fields.push({ field: 'status', from: before.status, to: now.status });
    if (now.priority !== before.priority) fields.push({ field: 'priority', from: before.priority, to: now.priority });
    if (now.due !== before.due) fields.push({ field: 'due date', from: before.due || '(none)', to: now.due || '(none)' });
    if (now.owner !== before.owner) fields.push({ field: 'owner', from: before.owner, to: now.owner });
    /* v2.11: full text (no truncation) for description/source/details */
    if (now.desc !== before.desc) fields.push({ field: 'description', from: before.desc || '(empty)', to: now.desc || '(empty)' });
    if (now.source !== before.source) fields.push({ field: 'source', from: before.source || '(empty)', to: now.source || '(empty)' });
    if (now.date !== before.date) fields.push({ field: 'date raised', from: before.date || '(none)', to: now.date || '(none)' });
    if (now.details !== before.details) fields.push({ field: 'details', from: before.details || '(empty)', to: now.details || '(empty)' });
    /* v2.40: labels (array) diff — compare by sorted-join, treat as one change */
    {
      const beforeLabels = Array.isArray(before.labels) ? before.labels : [];
      const afterLabels = Array.isArray(now.labels) ? now.labels : [];
      const sigBefore = [...beforeLabels].sort().join('|');
      const sigAfter = [...afterLabels].sort().join('|');
      if (sigBefore !== sigAfter) {
        fields.push({ field: 'labels', from: beforeLabels.join(', ') || '(none)', to: afterLabels.join(', ') || '(none)' });
      }
    }
    if ((now.requestor || '') !== (before.requestor || '')) fields.push({ field: 'requested by', from: before.requestor || '(none)', to: now.requestor || '(none)' });
    /* v3.2: six newly-tracked fields. Structured/data-driven output — the diff
       feeds the AI minutes-consolidation chat, so each is a terse, parseable
       { field, from, to } with consistent placeholder tokens. */
    if ((now.closedDate || '') !== (before.closedDate || '')) {
      fields.push({ field: 'closed date', from: before.closedDate || '(none)', to: now.closedDate || '(none)' });
    }
    {
      /* dependsOn / relatedTo: numeric-id arrays. Compare by sorted-join,
         report as #-prefixed id lists (one change each). */
      const fmtIds = arr => (Array.isArray(arr) && arr.length) ? arr.map(n => '#' + n).join(', ') : '(none)';
      const depB = Array.isArray(before.dependsOn) ? [...before.dependsOn].sort((x, y) => x - y) : [];
      const depA = Array.isArray(now.dependsOn) ? [...now.dependsOn].sort((x, y) => x - y) : [];
      if (depB.join('|') !== depA.join('|')) {
        fields.push({ field: 'depends on', from: fmtIds(depB), to: fmtIds(depA) });
      }
      const relB = Array.isArray(before.relatedTo) ? [...before.relatedTo].sort((x, y) => x - y) : [];
      const relA = Array.isArray(now.relatedTo) ? [...now.relatedTo].sort((x, y) => x - y) : [];
      if (relB.join('|') !== relA.join('|')) {
        fields.push({ field: 'related to', from: fmtIds(relB), to: fmtIds(relA) });
      }
    }
    {
      /* progress: number 0-100 or null. Report null as "(none)", else "N%". */
      const pB = (typeof before.progress === 'number') ? before.progress : null;
      const pA = (typeof now.progress === 'number') ? now.progress : null;
      if (pB !== pA) {
        fields.push({ field: 'progress', from: (pB === null ? '(none)' : pB + '%'), to: (pA === null ? '(none)' : pA + '%') });
      }
    }
    if (!!now.flagged !== !!before.flagged) {
      fields.push({ field: 'on agenda', from: before.flagged ? 'yes' : 'no', to: now.flagged ? 'yes' : 'no' });
    }
    {
      /* parentId: number (sub-action under that id) or null (top-level). */
      const parB = (typeof before.parentId === 'number') ? before.parentId : null;
      const parA = (typeof now.parentId === 'number') ? now.parentId : null;
      if (parB !== parA) {
        fields.push({ field: 'parent action', from: (parB === null ? '(top-level)' : '#' + parB), to: (parA === null ? '(top-level)' : '#' + parA) });
      }
    }
    /* v2.11: compare actual notes by noteId (stable identifier) to detect both
       additions and removals, and capture the actual text (not just counts). */
    {
      const beforeIds = new Set((before.updates || []).map(u => u.noteId).filter(Boolean));
      const nowIds    = new Set((now.updates    || []).map(u => u.noteId).filter(Boolean));
      const addedNotes   = (now.updates    || []).filter(u => u.noteId && !beforeIds.has(u.noteId));
      const removedNotes = (before.updates || []).filter(u => u.noteId && !nowIds.has(u.noteId));
      if (addedNotes.length || removedNotes.length) {
        fields.push({ field: 'meeting updates', addedNotes, removedNotes });
      }
    }
    if (fields.length) changes.push({ id: Number(id), desc: now.desc, type: 'changed', fields });
  });
  // check removed
  Object.keys(snapshot).forEach(id => {
    if (!current[id]) changes.push({ id: Number(id), desc: snapshot[id].desc, type: 'removed', fields: [] });
  });
  return changes;
}

/* ── SESSIONS ── */
function addSession() {
  markDirty();
  syncSessionsFromDOM();  // capture any pending input before renderSessions wipes the DOM
  const today = todayISO();
  const session = {
    id: _nextMtgSeq(),   /* v16.8 (W5) */
    date: today,
    startTime: '',
    endTime: '',
    title: '',
    attendees: '',
    attendeeRows: [ { name: '', organisation: '' } ],
    minutes: '',
    diff: []
    /* v13.1 (A41): `isNew: true` used to be stamped here. A census of the shell finds no
       reader for it anywhere — write-only, and it rode into the data file on every meeting
       ever created (2 on disk). Not written any more; existing ones are dropped lazily on
       project activation. (The `isNew` in the stakeholder drawer is an unrelated local.) */
  };
  SESSIONS.unshift(session);
  /* v3.0.2: the baseline (sessionSnapshot) is intentionally NOT moved here.
     Adding a meeting does not reset the tracker baseline — only an explicit
     resetTracker() click does. This was the v2.40 design intent for the
     "moves on addSession" path that was never actually implemented; users
     came to prefer the explicit-only behavior, and v3.0.2 makes it official. */
  renderSessions();
  const card = document.getElementById('session-'+session.id);
  if(card) { card.classList.add('expanded'); card.querySelector('.session-date-input').focus(); }
}

/* v2.16: duplicate the most recent meeting — copy attendees / title / time,
   reset date to today and clear minutes/summary/diff so the new entry is a
   clean draft with the recurring metadata pre-filled. */
function duplicateLatestSession() {
  if (!SESSIONS.length) return;  // button is disabled in this state, defensive guard anyway
  markDirty();
  syncSessionsFromDOM();  // capture any pending input before renderSessions wipes the DOM
  /* v14 (W4b): ONE definition of "latest", shared with the save capture, the meeting timer and
     the Latest badge. This held its own copy of the same date-desc-then-id-desc sort — identical
     logic, but a second copy free to drift, and it is where that tie-break was first written. */
  const latest = _mostRecentSession();
  if (!latest) return;
  // Deep copy the attendee rows so edits to the new session don\'t bleed back.
  /* ⚠ v16 (b): `optional` IS carried, `present` is deliberately NOT — and the asymmetry is the
     point, not an oversight. Optional describes the INVITATION, so duplicating a recurring
     meeting should keep it; present describes ATTENDANCE at one specific meeting, so a fresh
     copy must start with nobody marked absent. Dropping `present` here is pre-existing and
     correct; this comment exists so nobody "fixes" the pair into agreeing.
     ⚠ Note this rebuilds each row from an object LITERAL — the normalizeAppMeta hazard. Any
     future per-attendee key has to be added here as well or it vanishes on duplication. */
  const clonedAttendees = (latest.attendeeRows || []).map(r => Object.assign(
    { name: r.name || '', organisation: r.organisation || '' },
    r.optional === true ? { optional: true } : {}
  ));
  const session = {
    id: _nextMtgSeq(),   /* v16.8 (W5) */
    date: todayISO(),
    startTime: latest.startTime || '',
    endTime: latest.endTime || '',
    /* v15.2: the times were copied and their TIMEZONE was not — duplicating a recurring
       Munich meeting dropped the “CEST / Munich” v14.2 attached to exactly those times, and
       the copy printed them unlabelled. Conditional so a meeting without one stays lazy. */
    ...(latest.tz ? { tz: latest.tz } : {}),
    title: latest.title || '',
    attendees: '',  // recomputed below from attendeeRows
    attendeeRows: clonedAttendees.length ? clonedAttendees : [{ name: '', organisation: '' }],
    minutes: '',
    actionSummary: '',
    diff: []
    /* v13.1 (A41): the second of the two creation paths — see addSession. */
  };
  session.attendees = formatAttendeesPreview(session.attendeeRows);
  SESSIONS.unshift(session);
  /* v3.0.2: same as addSession — baseline is NOT moved here. Only resetTracker
     moves the baseline. */
  renderSessions();
  const card = document.getElementById('session-' + session.id);
  if (card) {
    card.classList.add('expanded');
    const dateInput = card.querySelector('.session-date-input');
    if (dateInput) dateInput.focus();
  }
}

function deleteSession(id) {
  const s = SESSIONS.find(x => x.id === id);
  if (!s) return;
  const label = (s.title || '(Untitled meeting)') + ' · ' + formatDateDisplay(s.date);
  showConfirm(
    'Delete meeting?',
    'This meeting entry and its notes will be removed:<br><br><strong>' + esc(label) + '</strong>',
    () => {
      markDirty();
      syncSessionsFromDOM();  // capture any pending input before renderSessions wipes the DOM
      /* v2.22: capture for undo BEFORE splicing */
      const idx = SESSIONS.findIndex(x => x.id === id);
      if (idx === -1) { renderSessions(); return; }
      const savedSession = JSON.parse(JSON.stringify(SESSIONS[idx]));
      const savedIdx = idx;
      SESSIONS.splice(idx, 1);
      renderSessions();

      /* v2.22: show undo toast */
      const restoreFn = () => {
        SESSIONS.splice(savedIdx, 0, savedSession);
        markDirty();
        renderSessions();
        showToast('Meeting restored', { type: 'info' });
      };
      const labelShort = (savedSession.title || '(Untitled)').slice(0, 50);
      showUndoToast('Deleted meeting: ' + labelShort, restoreFn);
    }
  );
}

/* ⚠ v14.2 (c): THE SEEDING IS GONE, on his explicit instruction — "Dont touch stored values,
   I will change manually in the app." v14.1 stamped this machine's IANA zone on a meeting the
   moment a time was typed, which is what made the field arrive pre-filled with a value that
   matched nothing else in the list. A new meeting now starts BLANK, with a placeholder that
   teaches the field in three words: "e.g. CEST / Munich".
   ⚠ Meetings v14.1 already stamped keep their Europe/Berlin until he edits them himself. This
   repo does not silently rewrite stored values, and renaming one teaches the list. */
function saveSessionField(id, field, value) {
  markDirty();
  const s = SESSIONS.find(s=>s.id===id);
  if(s) s[field] = value;
}

function toggleSession(id) { document.getElementById('session-'+id).classList.toggle('expanded'); syncExpandToggleLabels(); }

// Session sort direction state — true = newest first (default), false = oldest first
let sessionSortDesc = true;
/* v2.6: session-list filter (NOT persisted, session-state only) */
let sessionSearchQuery = '';

function toggleSessionSort() {
  syncSessionsFromDOM();  // capture any pending input before renderSessions wipes the DOM
  sessionSortDesc = !sessionSortDesc;
  // Update button label and arrow icon
  const lbl = document.getElementById('sessionSortLabel');
  const ico = document.getElementById('sessionSortIcon');
  if (lbl) lbl.textContent = sessionSortDesc ? 'Newest first' : 'Oldest first';
  if (ico) ico.innerHTML = sessionSortDesc
    ? '<path d="M6 1v9M3 7l3 3 3-3"/>'
    : '<path d="M6 11V2M3 5l3-3 3 3"/>';
  renderSessions();
}

/* ── ⚠ v13.1 (A23): ONE definition of "the latest meeting" ──────────────────────────────
   Two lived side by side and disagreed. The "Latest" BADGE was computed chronologically, by
   date, deliberately independent of the display sort. generateActionSummary decided which
   meeting owns the live snapshot baseline with `s.id === SESSIONS[0].id` — ARRAY order, i.e.
   whichever meeting happens to sit first in the array. Those differ the moment you add a
   meeting dated earlier than one already recorded, which is ordinary: minute last week's
   catch-up after today's. The consequence was that "Generate from action changes" on the
   meeting the app itself badges Latest fell through to that meeting's STORED diff, while the
   running record of everything changed since the meeting began was attributed to a different
   meeting entirely. Both now ask this function. */
/* ══ ⚠ v14 (W4b): THE ARRAY-ORDER FIX W1 PREDICTED AND PARKED ══════════════════════════════
   The merge SORTS every collection by its key so that the result is a function of the SET of
   records rather than of the order the parts happened to sync in — that is what makes it
   commutative. W1 flagged the consequence and left it to be VERIFIED here rather than assumed:
   after a merge, array order is no longer insertion order.

   Sessions are the place it bites. New meetings were UNSHIFTED to the front, so `SESSIONS[0]`
   meant "the newest meeting" throughout the shell — and once sessions are sorted by id ascending
   it means the LOWEST ID, i.e. the OLDEST. Three sites still read it that way, and one of them was
   inside the save capture (`SESSIONS[0].diff = computeDiff(...)`), so every save would have written
   the current meeting's action diff onto the oldest meeting in the project. Silent, permanent, and
   invisible to any single-writer test.

   v13.1 (A23) had already introduced this chronological definition to fix one such site. It is now
   the ONLY definition of "the current meeting" in the shell, and it returns the RECORD as well as
   the id because two of the three callers need to write to it.
   ⚠ Tie-break added here: date descending, then ID descending. Two meetings on the same date used
   to resolve by insertion order (newest first); with the merge that would resolve by whichever
   part sorted first, which differs per machine and would not converge. Newest id wins, which
   matches the old intent and is computed identically everywhere. */
function _mostRecentSession() {
  if (!Array.isArray(SESSIONS) || !SESSIONS.length) return null;
  return [...SESSIONS].sort((a, b) => {
    const d = String((b && b.date) || '').localeCompare(String((a && a.date) || ''));
    if (d) return d;
    return Number((b && b.id) || 0) - Number((a && a.id) || 0);
  })[0] || null;
}
function _mostRecentSessionId() {
  const s = _mostRecentSession();
  return s ? s.id : null;
}
function renderSessions() {
  syncDuplicateBtnState();  // v2.16: keep Duplicate-latest button enabled state in sync
  const list = document.getElementById('sessionList');
  const empty = document.getElementById('sessionEmpty');
  /* ⚠ v13.1 (A63): set the WORDS here, not just the visibility. This branch only toggled
     display, so whatever the filtered branch below last wrote into the element survived —
     delete your last meeting after having searched for something, and the empty state still
     read "No meetings match your search" with no search active and nothing left to search.
     Unconditional on purpose: with no meetings at all, the search is not the reason. */
  if(!SESSIONS.length) { list.innerHTML=''; empty.textContent='No meetings recorded yet. Add one to get started.'; empty.style.display='block'; return; }
  empty.style.display='none';
  const expandedIds = new Set([...document.querySelectorAll('.session-card.expanded')].map(el=>Number(el.dataset.id)));
  // Compute "Latest" badge from chronologically newest session, regardless of display sort direction
  const mostRecentId = _mostRecentSessionId();
  // Apply user-selected sort direction for display
  /* v15.2: the same id tie-break _mostRecentSession has — without it, two meetings on one
     date could show the "Latest"-badged one BELOW its sibling after a merge/reload, the badge
     and the ordering disagreeing about the same fact. */
  let displayed = [...SESSIONS].sort((a,b) => {
    const d = sessionSortDesc ? (b.date||'').localeCompare(a.date||'') : (a.date||'').localeCompare(b.date||'');
    if (d) return d;
    return sessionSortDesc ? (Number(b.id)||0) - (Number(a.id)||0) : (Number(a.id)||0) - (Number(b.id)||0);
  });
  // v2.6: filter by sessionSearchQuery if set (case-insensitive, matches title/minutes/attendees/actionSummary/date)
  if (sessionSearchQuery) {
    const q = sessionSearchQuery;
    displayed = displayed.filter(s => {
      const hay = [
        s.title || '',
        s.minutes || '',
        s.attendees || '',
        s.actionSummary || '',
        formatDateDisplay(s.date) || ''
      ].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }
  if (!displayed.length) {
    list.innerHTML = '';
    empty.textContent = sessionSearchQuery ? 'No meetings match your search.' : 'No meetings recorded yet. Add one to get started.';
    empty.style.display = 'block';
    return;
  }
  empty.style.display = 'none';

  list.innerHTML = displayed.map(s=>{
    const isExpanded = expandedIds.has(s.id);
    const isCurrent = s.id === mostRecentId;
    const attendeePreview = s.attendees ? s.attendees : 'No attendees recorded';
    const wordCount = s.minutes ? s.minutes.trim().split(/\s+/).filter(Boolean).length : 0;
    const diffCount = (s.diff || []).length;
    const titleDisplay = s.title || '(Untitled meeting)';
    const diffHtml = diffCount ? renderDiff(s.diff, s.id) : '<div class="diff-none">No action changes recorded for this session.</div>';
    return `
    <div class="session-card${isCurrent?' current':''}${isExpanded?' expanded':''}" id="session-${s.id}" data-id="${s.id}">
      <div class="session-header" onclick="toggleSession(${s.id})">
        <div class="session-date-wrap">
          <div class="session-title-display">${esc(titleDisplay)}</div>
          <div class="session-title-sub">${esc(formatDateDisplay(s.date))}${formatTimeRange(s) ? ' · ' + esc(formatTimeRange(s)) : ''}</div>
        </div>
        ${isCurrent?'<span class="session-current-tag">Latest</span>':''}
                ${wordCount?`<span class="meta-text" style="font-size:11px;white-space:nowrap">${wordCount} word${wordCount===1?'':'s'}</span>`:''}
        <div class="session-chevron"><svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M4.5 7L9 11.5L13.5 7"/></svg></div>
        <button class="session-del-btn" title="Delete this session" onclick="deleteSession(${s.id});event.stopPropagation()"><svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M4 4l10 10M14 4l-10 10"/></svg></button>
      </div>
      <div class="session-body">
        <div class="session-field">
          <div class="session-field-label">Meeting title</div>
          <input class="session-title-input" type="text" value="${esc(s.title || '')}" placeholder="e.g. Service Meeting, Ad-hoc credits call, Autopilot workshop…" oninput="saveSessionField(${s.id},'title',this.value);updateSessionHeader(${s.id})" onclick="event.stopPropagation()">
        </div>
        <div class="field-row" style="margin-bottom:16px">
          <div class="session-field" style="max-width:180px;margin-bottom:0">
            <div class="session-field-label">Date</div>
            <input class="session-date-input" type="date" value="${esc(s.date || '')}" style="width:100%" onclick="event.stopPropagation()" onchange="saveSessionField(${s.id},'date',this.value);updateSessionHeader(${s.id})">
          </div>
          <div class="session-field" style="max-width:120px;margin-bottom:0">
            <div class="session-field-label">Start time</div>
            <input class="time-input" type="time" value="${esc(s.startTime||'')}" onclick="event.stopPropagation()" onchange="saveSessionField(${s.id},'startTime',this.value);updateSessionHeader(${s.id})">
          </div>
          <div class="session-field" style="max-width:120px;margin-bottom:0">
            <div class="session-field-label">End time</div>
            <input class="time-input" type="time" value="${esc(s.endTime||'')}" onclick="event.stopPropagation()" onchange="saveSessionField(${s.id},'endTime',this.value);updateSessionHeader(${s.id})">
          </div>
          <!-- v14.1 (h): ONE zone per MEETING, not per field - start and end are one
               occasion, and two pickers could only disagree. The datalist idiom already
               exists here for organisations, stakeholders and labels.
               ⚠ v14.2 (c): FREE TEXT. The list is now the labels HE has used, not 418 IANA
               names, and nothing is refused. -->
          <div class="session-field" style="max-width:190px;margin-bottom:0">
            <div class="session-field-label">Timezone</div>
            <input class="session-date-input" type="text" list="timezoneList" style="width:100%"
                   value="${esc(s.tz||'')}" placeholder="e.g. CEST / Munich"
                   onclick="event.stopPropagation()" onchange="saveSessionZone(${s.id},this)"
                   onblur="saveSessionZone(${s.id},this)">
          </div>
        </div>
        <div class="session-field">
          <div class="session-field-label">Attendees</div>
          ${(s.attendeeRows||[]).map((r, i, arr) => `
            <div class="attendee-group">
              <input class="attendee-present" type="checkbox" ${(r.present !== false) ? 'checked' : ''} title="Present (untick = apologies / absent)" onchange="toggleAttendeePresent(${s.id},${i},this.checked)" onclick="event.stopPropagation()">
              <button type="button" class="att-opt" aria-pressed="${r.optional === true ? 'true' : 'false'}" ${((r.name || '').trim()) ? '' : 'disabled'} title="Optional attendee — did not have to be here. Separate from the tick, which records who actually came." onclick="toggleAttendeeOptional(${s.id},${i});event.stopPropagation()">optional</button>
              <input class="attendee-organisation" type="text" value="${esc(r.organisation||'')}" placeholder="Organisation" list="organisationList" oninput="saveAttendeeField(${s.id},${i},'organisation',this.value)" onchange="refilterAttendeeNames(${s.id},${i},this.value)" onfocus="this.select()" onclick="event.stopPropagation()">
              <input class="attendee-name" type="text" value="${esc(r.name||'')}" placeholder="Name" list="attNames_${s.id}_${i}" oninput="saveAttendeeField(${s.id},${i},'name',this.value)" onclick="event.stopPropagation()">
              <datalist id="attNames_${s.id}_${i}">${_attendeeNameOptionsHTML(r.organisation)}</datalist>
              <button class="attendee-group-move" title="Move up" onclick="moveAttendeeRow(${s.id},${i},-1);event.stopPropagation()" ${i === 0 ? 'disabled' : ''}><svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3 7l3-3 3 3"/></svg></button>
              <button class="attendee-group-move" title="Move down" onclick="moveAttendeeRow(${s.id},${i},1);event.stopPropagation()" ${i === arr.length - 1 ? 'disabled' : ''}><svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3 5l3 3 3-3"/></svg></button>
              <button class="attendee-group-del" title="Remove this attendee" onclick="removeAttendeeRow(${s.id},${i});event.stopPropagation()"><svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M2 2l8 8M10 2l-8 8"/></svg></button>
            </div>`).join('')}
          <button class="add-organisation-btn" onclick="addAttendeeRow(${s.id});event.stopPropagation()">
            <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M6 1v10M1 6h10"/></svg>
            Add attendee
          </button>
        </div>
        <div class="session-field">
          <div class="session-field-label">Action summary for Teams ${isCurrent?'<span class="live-tag">current meeting</span>':''}</div>
          ${isCurrent ? `
            <div class="action-summary-controls">
              <button class="summary-btn" onclick="generateActionSummary(${s.id});event.stopPropagation()">Generate from action changes</button>
              <button class="summary-btn summary-btn-secondary" onclick="copyMeetingPost(${s.id});event.stopPropagation()">Copy full meeting post</button>
              <button class="summary-btn summary-btn-secondary reset-tracker-btn" id="resetTrackerBtn-${s.id}" onclick="resetTracker();event.stopPropagation()" title="${_hasResetTrackerChanges() ? 'Take a fresh snapshot — future summaries will only include changes from this point onward.' : 'No changes since last baseline'}"${_hasResetTrackerChanges() ? '' : ' disabled'}>Reset tracker</button>
            </div>
            <textarea class="action-summary-output" onclick="event.stopPropagation()" oninput="saveSessionField(${s.id},\'actionSummary\',this.value)" placeholder="Click Generate to populate this with bullet points of action changes from this meeting. You can edit the result before posting to Teams.">${esc(s.actionSummary||'')}</textarea>
          ` : `
            <!-- v2.40: past-meeting summary unlock — editable textarea + Copy on all meetings -->
            <div class="action-summary-controls">
              <button class="summary-btn summary-btn-secondary" onclick="copyMeetingPost(${s.id});event.stopPropagation()">Copy full meeting post</button>
            </div>
            <textarea class="action-summary-output" onclick="event.stopPropagation()" oninput="saveSessionField(${s.id},\'actionSummary\',this.value)" placeholder="Type a summary for this past meeting…">${esc(s.actionSummary||'')}</textarea>
          `}
        </div>
        <div class="session-field">
          <!-- ⚠ v15.2: THIS PANEL EXISTED SINCE v8.5 AND HAD NEVER DRAWN. renderSessions built
               diffHtml on every render and the template never interpolated it — a census found
               ONE occurrence of the variable in the whole shell — so the stored per-meeting
               action-change history was invisible, its per-entry × curation was unreachable
               UI, and v14.2 (b)'s id conversion inside it landed in markup that could not
               reach a screen. The label is honest about the current meeting: its list is
               rebuilt from the baseline at every save, so an × there holds only until then. -->
          <div class="session-field-label">Action changes ${isCurrent?'<span class="live-tag">rebuilt at each save</span>':''}</div>
          ${diffHtml}
        </div>
        <div class="session-field">
          <div class="session-field-label">Minutes <span class="live-tag">running document</span></div>
          <textarea class="minutes-textarea" placeholder="Type meeting notes here — agenda items discussed, decisions made, context for actions, open questions…" oninput="saveSessionField(${s.id},'minutes',this.value);updateSessionHeader(${s.id})" onclick="event.stopPropagation()">${esc(s.minutes)}</textarea>
          <div class="char-hint">${wordCount} word${wordCount===1?'':'s'}</div>
        </div>
        <div class="session-field">
          <div class="session-field-label">Next meeting</div>
          <div class="field-row">
            <div class="session-field" style="max-width:180px;margin-bottom:0">
              <div class="session-field-label">Date</div>
              <input class="session-date-input nm-date" type="date" value="${esc(s.nextMeetingDate||'')}" style="width:100%" onclick="event.stopPropagation()" onchange="saveSessionField(${s.id},'nextMeetingDate',this.value)">
            </div>
            <div class="session-field" style="max-width:120px;margin-bottom:0">
              <div class="session-field-label">Start time</div>
              <input class="time-input nm-time" type="time" value="${esc(s.nextMeetingTime||'')}" onclick="event.stopPropagation()" onchange="saveSessionField(${s.id},'nextMeetingTime',this.value)">
            </div>
            <div class="session-field" style="max-width:140px;margin-bottom:0">
              <div class="session-field-label">Planned end time</div>
              <input class="time-input nm-endtime" type="time" value="${esc(s.nextMeetingEndTime||'')}" onclick="event.stopPropagation()" onchange="saveSessionField(${s.id},'nextMeetingEndTime',this.value)">
            </div>
            <!-- ⚠ v14.2 (c): the next meeting's OWN zone. ◆h1 deliberately shared one zone and
                 wrote its own exit clause; he hit it on day one, so this is that new field.
                 Placeholder shows what it falls back to, so leaving it blank is obviously
                 safe - and blank is exactly what keeps the key out of the file. -->
            <div class="session-field" style="max-width:170px;margin-bottom:0">
              <div class="session-field-label">Timezone</div>
              <input class="session-date-input nm-tz" type="text" list="timezoneList" style="width:100%"
                     value="${esc(s.nextTz||'')}" placeholder="${esc(s.tz||'e.g. CEST / Munich')}"
                     onclick="event.stopPropagation()" onchange="saveSessionNextZone(${s.id},this)"
                     onblur="saveSessionNextZone(${s.id},this)">
            </div>
          </div>
        </div>
      </div>
    </div>`;
  }).join('');
  /* v3.0.2: apply search-result highlighting to the freshly-rendered sessions */
  highlightSearchMatches(list, sessionSearchQuery, _MINUTES_HIGHLIGHT_SELECTORS);
}

function updateSessionHeader(id) {
  const s = SESSIONS.find(x => x.id === id);
  if (!s) return;
  const card = document.getElementById('session-' + id);
  if (!card) return;
  const titleEl = card.querySelector('.session-title-display');
  const subEl = card.querySelector('.session-title-sub');
  if (titleEl) titleEl.textContent = s.title || '(Untitled meeting)';
  if (subEl) { const tr = formatTimeRange(s); subEl.textContent = formatDateDisplay(s.date) + (tr ? ' · ' + tr : ''); }
  const wc = s.minutes ? s.minutes.trim().split(/\s+/).filter(Boolean).length : 0;
  const hint = card.querySelector('.char-hint');
  if (hint) hint.textContent = wc + ' word' + (wc===1?'':'s');
}

function renderDiff(changes, sessionId) {
  // X-button HTML, identical for all three entry types — mirrors the attendee remove pattern
  const delBtn = i => `<button class="diff-entry-del" title="Remove this change entry" onclick="removeDiffEntry(${sessionId},${i});event.stopPropagation()"><svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M2 2l8 8M10 2l-8 8"/></svg></button>`;
  return changes.map((c, i) => {
    if (c.type === 'added') {
      return `<div class="diff-entry"><div class="diff-entry-id">${displayActionId(c.id)}</div><div class="diff-entry-body"><div class="diff-entry-desc">${esc(c.desc)}</div><div class="diff-entry-change"><span class="diff-to">Added new action</span></div></div>${delBtn(i)}</div>`;
    }
    if (c.type === 'removed') {
      return `<div class="diff-entry"><div class="diff-entry-id">${displayActionId(c.id)}</div><div class="diff-entry-body"><div class="diff-entry-desc">${esc(c.desc)}</div><div class="diff-entry-change"><span class="diff-from">Removed</span></div></div>${delBtn(i)}</div>`;
    }
    const changesHtml = c.fields.map(f => {
      if (f.field === 'notes') return `<div><strong>${esc(f.field)}:</strong> <span class="diff-to">${esc(f.to)}</span></div>`;
      return `<div><strong>${esc(f.field)}:</strong> <span class="diff-from">${esc(f.from)}</span> → <span class="diff-to">${esc(f.to)}</span></div>`;
    }).join('');
    return `<div class="diff-entry"><div class="diff-entry-id">${displayActionId(c.id)}</div><div class="diff-entry-body"><div class="diff-entry-desc">${esc(c.desc)}</div><div class="diff-entry-change">${changesHtml}</div></div>${delBtn(i)}</div>`;
  }).join('');
}

/* v2.14: manually reset the action tracker baseline.
   Replaces sessionSnapshot with a fresh snapshot of the current state.
   After reset, generateActionSummary only sees changes made AFTER this point.
   Preserves any user-typed s.actionSummary text — only the diff baseline changes.
   Clears the yellow .has-update highlights since they were tracked against the
   old baseline. Marks dirty so the user remembers to save the new baseline. */
/* v2.40: returns true when the current state differs from the baseline.
   Used to enable/disable the Reset tracker button. */
function _hasResetTrackerChanges() {
  if (!sessionSnapshot) return false;
  const current = takeSnapshot();
  const diff = computeDiff(sessionSnapshot, current);
  return diff.length > 0;
}

function resetTracker() {
  /* v15.2: the ONE native confirm() left in the shell — eleven other confirmations use the
     house modal (§v15 recorded it; taken up this round). Same words, same flow; a native
     dialog also froze the meeting timer's tick, which the modal does not. */
  showConfirm('Reset tracker?',
    'This takes a fresh snapshot of the current state as the new baseline. '
    + 'Future summaries will only include changes from this point onward. '
    + 'Any summary text you have already typed or generated is kept.<br><br>'
    + 'Save the file afterwards to persist the new baseline.',
    () => {
      syncSessionsFromDOM();
      sessionSnapshot = takeSnapshot();
      /* v3.0.2: removed `sessionSnapshotIsStale = false` — the flag was always
         false (declared dead in v2.40 but not actually removed). The real
         baseline-moving work is the takeSnapshot() above. */
      sessionEditedActionIds.clear();  // highlights were vs the old baseline
      markDirty();
      refreshAll();
      renderSessions();
    });
}

function generateActionSummary(sessionId) {
  markDirty();
  /* v2.7: rewritten. Groups by action (one bullet per action with nested
     details), shows from → to for every changed field, covers ALL action data
     fields without exception. Field labels in output match v2.5 UI rename. */
  const s = SESSIONS.find(x => x.id === sessionId);
  if (!s) return;
  syncSessionsFromDOM();
  /* v5.1.1 §2: the measured duration is prepended to this summary; the Copy post shows
     it in its header (de-duplicated). Reads wrapped s.durationSec or the live timer. */
  const _durSec = (typeof meetingDurationSec === 'function') ? meetingDurationSec(s) : 0;
  const _durLine = _durSec >= 1 ? `Meeting duration: ${formatDuration(_durSec)}` : '';
  const currentSnapshot = takeSnapshot();
  /* v2.19.1: the live diff (sessionSnapshot vs current) only makes sense when
     (a) we are summarizing the current/topmost session AND
     (b) sessionSnapshot is still a meaningful baseline for it (not reset by
     a different-day boot). Otherwise the stored s.diff is the authoritative
     record — frozen at the time of the last save. */
  /* v13.1 (A23): the same chronological definition the "Latest" badge paints — see
     _mostRecentSessionId. This read SESSIONS[0], which is array order, not date order. */
  const _isCurrentSession = s.id === _mostRecentSessionId();
  const _canUseLiveDiff = _isCurrentSession && sessionSnapshot;  /* v3.0.2: dropped !sessionSnapshotIsStale — flag was always false */
  const diff = _canUseLiveDiff ? computeDiff(sessionSnapshot, currentSnapshot) : (s.diff || []);

  if (diff.length === 0) {
    s.actionSummary = 'No action changes recorded for this meeting.' + (_durLine ? `\n\n${_durLine}` : '');
    renderSessions();
    return;
  }

  /* Display labels for internal field names (handles v2.5 rename) */
  const fieldDisplayName = {
    'status':           'Status',
    'priority':         'Priority',
    'due date':         'Due',
    'owner':            'Owner',
    'description':      'Title',         /* v2.5 rename: desc → Title */
    'source':           'Source',
    'date raised':      'Date raised',
    'details':          'Description',   /* v2.5 rename: details → Description */
    'labels':           'Labels',
    'requested by':     'Requested by',
    'meeting updates':  'Notes',
  };
  const displayLabel = (f) => fieldDisplayName[f] || (f.charAt(0).toUpperCase() + f.slice(1));

  const added   = diff.filter(c => c.type === 'added');
  const updated = diff.filter(c => c.type === 'changed' || (!c.type && (c.fields||[]).length));
  const removed = diff.filter(c => c.type === 'removed');

  const lines = ['Action items from this meeting:', ''];

  if (added.length) {
    lines.push('New:');
    added.forEach(c => {
      lines.push(`- ${_refPrefix(c.id)}${c.desc}`);
      const e = edits[c.id] || {};
      /* For new actions, list every populated field so the reader knows what was created */
      if (e.owner)            lines.push(`  Owner: ${e.owner}`);
      if (e.requestor)        lines.push(`  Requested by: ${e.requestor}`);
      if (e.status)           lines.push(`  Status: ${e.status}`);
      if (e.priority)         lines.push(`  Priority: ${e.priority}`);
      if (e.due)              lines.push(`  Due: ${e.due}`);
      if (e.date)             lines.push(`  Date raised: ${e.date}`);
      if (e.source)           lines.push(`  Source: ${e.source}`);
      if (Array.isArray(e.labels) && e.labels.length) lines.push(`  Labels: ${e.labels.join(', ')}`);
      /* v2.11: full text (no truncation) and actual note content */
      if (e.details)          lines.push(`  Description: ${e.details}`);
      if (e.updates && e.updates.length) {
        lines.push(`  Notes added:`);
        e.updates.forEach(u => lines.push(`    \u2022 ${u.text}`));
      }
    });
    lines.push('');
  }

  if (updated.length) {
    lines.push('Updated:');
    updated.forEach(c => {
      lines.push(`- ${_refPrefix(c.id)}${c.desc}`);
      (c.fields || []).forEach(f => {
        if (f.field === 'meeting updates') {
          /* v2.11: enumerate actual note text for both added and removed */
          if (f.addedNotes && f.addedNotes.length) {
            lines.push(`  Notes added:`);
            f.addedNotes.forEach(n => lines.push(`    \u2022 ${n.text}`));
          }
          if (f.removedNotes && f.removedNotes.length) {
            lines.push(`  Notes removed:`);
            f.removedNotes.forEach(n => lines.push(`    \u2022 ${n.text}`));
          }
        } else {
          lines.push(`  ${displayLabel(f.field)}: ${f.from} \u2192 ${f.to}`);
        }
      });
    });
    lines.push('');
  }

  if (removed.length) {
    lines.push('Removed:');
    removed.forEach(c => {
      lines.push(`- ${_refPrefix(c.id)}${c.desc}`);
    });
    lines.push('');
  }

  /* Trim trailing blank line */
  while (lines.length && lines[lines.length - 1] === '') lines.pop();

  if (_durLine) lines.unshift(_durLine, '');   // v5.1 §2: duration as the summary's first line
  s.actionSummary = lines.join('\n');
  renderSessions();
  const card = document.getElementById('session-' + sessionId);
  if (card) card.classList.add('expanded');
}

function copyMeetingPost(sessionId) {
  /* v2.5: combine minutes + action summary, copy to clipboard for Teams post.
     v2.16: now includes title, date, time range, and attendees table — full post. */
  const s = SESSIONS.find(x => x.id === sessionId);
  if (!s) return;
  // Sync first so we get the latest typed minutes / attendees / etc.
  syncSessionsFromDOM();
  const dateStr = formatDateDisplay(s.date);
  const title = s.title || 'Meeting';
  const parts = [`${title} — ${dateStr}`, ''];
  /* v13.1 (A46): the same day-first string the title line above already prints. This
     line used to emit the raw ISO date, so one short post carried two date
     conventions and appeared to disagree with itself. */
  parts.push(`Date: ${dateStr}`);
  // v2.18: time line in 12h AM/PM format with CEST/CET zone (date-aware)
  if (s.startTime || s.endTime) {
    const zone = (typeof s.tz === 'string') ? s.tz.trim() : '';   /* v14.2 (c) */
    const zoneSuffix = zone ? ' ' + zone : '';
    let timeLine;
    if (s.startTime && s.endTime) timeLine = to12h(s.startTime) + ' – ' + to12h(s.endTime) + zoneSuffix;
    else if (s.startTime)         timeLine = to12h(s.startTime) + zoneSuffix;
    else                          timeLine = '– ' + to12h(s.endTime) + zoneSuffix;
    parts.push(`Time: ${timeLine}`);
  }
  /* v5.1.1 §2: always show the measured duration in the post header (independent of
     whether the action summary was generated). De-duplicated below: if the embedded
     summary also carries a "Meeting duration:" line, that line is stripped. */
  {
    const _durSec = (typeof meetingDurationSec === 'function') ? meetingDurationSec(s) : 0;
    if (_durSec >= 1) parts.push(`Meeting duration: ${formatDuration(_durSec)}`);
  }
  parts.push('');
  // Attendees as a Name<TAB>Organisation<TAB>Optional table — present under
  // "Attendees", absent under "Apologies". Optional stays independent of presence.
  const _attRows = (s.attendeeRows || []).filter(r => (r.name && r.name.trim()) || (r.organisation && r.organisation.trim()));
  const _present = _attRows.filter(r => r.present !== false);
  const _absent  = _attRows.filter(r => r.present === false);
  if (_present.length) {
    parts.push('Attendees:');
    parts.push('');
    parts.push('Name\tOrganisation\tOptional');
    _present.forEach(r => {
      parts.push((r.name||'').trim() + '\t' + (r.organisation||'').trim()
        + '\t' + (r.optional === true ? 'Yes' : ''));
    });
    parts.push('');
  }
  if (_absent.length) {
    parts.push('Apologies:');
    parts.push('');
    parts.push('Name\tOrganisation\tOptional');
    _absent.forEach(r => {
      parts.push((r.name||'').trim() + '\t' + (r.organisation||'').trim()
        + '\t' + (r.optional === true ? 'Yes' : ''));
    });
    parts.push('');
  }
  if (s.minutes && s.minutes.trim()) {
    parts.push('Minutes');
    parts.push(s.minutes.trim());
    parts.push('');
  }
  /* v5.3: next meeting — placed directly beneath the minutes, mirroring where the
     field sits in the UI. Same style as the header time line (day-first date, 12h
     AM/PM, CET/CEST zone). Shown only when a date is set; the planned-end gives a
     start–end window when present. */
  {
    const nmDate = (typeof s.nextMeetingDate === 'string' ? s.nextMeetingDate : '').trim();
    if (nmDate) {
      const nmStart = (typeof s.nextMeetingTime === 'string' ? s.nextMeetingTime : '').trim();
      const nmEnd   = (typeof s.nextMeetingEndTime === 'string' ? s.nextMeetingEndTime : '').trim();
      /* v14.2 (c): the next meeting's OWN zone when it has one, otherwise this meeting's. */
      const nmZone  = ((typeof s.nextTz === 'string' && s.nextTz.trim())
                       || (typeof s.tz === 'string' ? s.tz.trim() : '') || '');
      const nmZ     = nmZone ? ' ' + nmZone : '';
      let when = formatDateDisplay(nmDate);
      if (nmStart && nmEnd) when += ', ' + to12h(nmStart) + ' – ' + to12h(nmEnd) + nmZ;
      else if (nmStart)     when += ', ' + to12h(nmStart) + nmZ;
      else if (nmEnd)       when += ', – ' + to12h(nmEnd) + nmZ;
      parts.push('Next meeting: ' + when);
      parts.push('');
    }
  }
  if (s.actionSummary && s.actionSummary.trim()) {
    /* v5.1.1 §2: strip any "Meeting duration:" line from the embedded summary so it
       isn't duplicated with the header duration line above. */
    const _sum = s.actionSummary.trim()
      .replace(/^Meeting duration:[^\n]*\n\n?/, '')
      .replace(/\n\nMeeting duration:[^\n]*$/, '')
      .replace(/^Meeting duration:[^\n]*$/, '')
      .trim();
    if (_sum) parts.push(_sum);
  }
  // Trim trailing blank lines
  while (parts.length && parts[parts.length - 1] === '') parts.pop();
  const text = parts.join('\n');
  navigator.clipboard.writeText(text).then(() => {
    const btn = event && event.target ? event.target : null;
    if (btn) {
      const orig = btn.textContent;
      btn.textContent = 'Copied!';
      setTimeout(() => { btn.textContent = orig; }, 1500);
    }
  }).catch(err => {
    console.error('Copy failed:', err);
    alert('Could not copy to clipboard. Please select and copy manually.');
  });
}

/* v5.0 (1g-2): Copy the active project's agenda-flagged actions as compact plain
   text for pasting into Claude (agenda prep) — without exposing the whole data file.
   Variant A family rules: a flagged top-level action includes its WHOLE family
   (parent + every sub-action); a flagged sub whose parent is NOT flagged pulls in
   the parent as context plus only the flagged sub(s). Every populated field is
   included and update-notes are never truncated. */
function copyAgenda(btn) {
  const all = getAllActions();
  const byId = new Map(all.map(a => [a.id, a]));
  const fl = a => !!(edits[a.id] && edits[a.id].flagged === true);
  const topOf = a => { const e = edits[a.id] || {}; return e.parentId == null; };

  if (!all.some(fl)) {
    if (btn) { const o = btn.textContent; btn.textContent = 'No agenda items'; setTimeout(() => { btn.textContent = o; }, 1600); }
    return;
  }

  const lines = [];
  const done = new Set();
  const depList = ids => (ids || []).map(id => {
    const d = byId.get(id), de = edits[id] || {};
    const t = d ? (actField(de, d, 'desc') || '').trim() : '';
    return displayActionId(id) + (t ? ' ' + t : '');
  }).join(', ');

  const fmt = (a, pad, marker) => {
    if (done.has(a.id)) return;
    done.add(a.id);
    const e = edits[a.id] || {};
    const title = (actField(e, a, 'desc') || '').trim() || '(untitled)';
    lines.push(pad + marker + ' ' + title);
    const meta = [];
    if (e.priority) meta.push('Priority: ' + e.priority);
    /* v13.1 (A46): day-first, like the update-note stamps below it (nowStamp is
       en-GB) — the agenda copy-out mixed ISO due dates with day-first note times. */
    meta.push('Due: ' + formatDateDisplay(e.due));
    let st = (e.status || '').trim();
    if (hasOpenBlocker(a.id)) st = (st ? st + ' ' : '') + '(BLOCKED)';
    if (st) meta.push('Status: ' + st);
    lines.push(pad + '  ' + meta.join('  |  '));
    if (e.details && e.details.trim()) lines.push(pad + '  ' + e.details.trim());
    if (Array.isArray(e.labels) && e.labels.length) lines.push(pad + '  Labels: ' + e.labels.join(', '));
    if (Array.isArray(e.dependsOn) && e.dependsOn.length) lines.push(pad + '  Depends on: ' + depList(e.dependsOn));
    if (Array.isArray(e.relatedTo) && e.relatedTo.length) lines.push(pad + '  Related: ' + depList(e.relatedTo));
    if (Array.isArray(e.updates) && e.updates.length) {
      lines.push(pad + '  Notes:');
      e.updates.forEach(u => lines.push(pad + '   \u2022 ' + (u.time ? u.time + ' \u2014 ' : '') + (u.text || '').trim()));
    }
  };

  let first = true;
  const block = (parent, subIds) => {
    if (!first) lines.push('');
    first = false;
    fmt(parent, '', '\u25A0');                                   // top-level marker
    subIds.forEach(sid => {
      const s = byId.get(sid);
      if (s && !done.has(s.id)) { lines.push(''); fmt(s, '    ', '\u2514'); }  // sub-action marker
    });
  };

  // Variant A walk: one entry per in-agenda top-level family.
  all.filter(topOf).forEach(p => {
    const subIds = getSubActionIds(p.id);
    if (fl(p)) {
      block(p, subIds);                                          // flagged parent → whole family
    } else {
      const flaggedSubs = subIds.filter(id => { const s = byId.get(id); return s && fl(s); });
      if (flaggedSubs.length) block(p, flaggedSubs);             // parent as context + flagged subs only
    }
  });
  // Safety net: any flagged action still unrendered (e.g. an orphaned sub whose
  // parent was deleted) → emit standalone so nothing on the agenda is ever dropped.
  all.forEach(a => { if (fl(a) && !done.has(a.id)) { if (!first) lines.push(''); first = false; fmt(a, '', '\u25A0'); } });

  const text = lines.join('\n');
  navigator.clipboard.writeText(text).then(() => {
    if (btn) { const o = btn.textContent; btn.textContent = 'Copied!'; setTimeout(() => { btn.textContent = o; }, 1500); }
  }).catch(err => { console.error('Copy agenda failed:', err); alert('Could not copy the agenda to the clipboard. Please copy manually.'); });
}

function removeDiffEntry(sessionId, diffIndex) {
  markDirty();
  syncSessionsFromDOM();  // capture any pending input before renderSessions wipes the DOM
  const s = SESSIONS.find(x => x.id === sessionId);
  if (!s || !Array.isArray(s.diff) || !s.diff[diffIndex]) return;
  s.diff.splice(diffIndex, 1);
  renderSessions();
  // Keep the card open so the user doesn't lose their place
  const card = document.getElementById('session-' + sessionId);
  if (card) card.classList.add('expanded');
}

/* P2: comma-token suggestions belong to one open modal and project UID. */
function _actionLabelUid() {
  const project = ((window.__DB__ || {}).projects || []).find(p => p.id === activeProjectId);
  return project && project.uid;
}
function _actionLabelsCurrent(ctx) {
  return !!(ctx && _actionLabelContext === ctx && ctx.uid && ctx.uid === _actionLabelUid()
    && ctx.input === document.getElementById('m-label') && ctx.input.isConnected
    && ctx.modal.classList.contains('open') && ctx.input.getClientRects().length
    && getComputedStyle(ctx.input).visibility === 'visible');
}
function _actionLabelToken(input) {
  const value = input.value, start = input.selectionStart, end = input.selectionEnd;
  if (start == null || end == null || value.slice(start, end).includes(',')) return null;
  const left = start === 0 ? 0 : value.lastIndexOf(',', start - 1) + 1;
  const next = value.indexOf(',', start), right = next < 0 ? value.length : next;
  const token = value.slice(left, right);
  const used = new Set((value.slice(0, left) + value.slice(right)).split(',').map(s => s.trim().toLowerCase()));
  return { value, start, end, left, right, token, used };
}
function _actionLabelMatches(token) {
  if (!token) return [];
  const query = token.token.trim().toLowerCase();
  return [...document.querySelectorAll('#labelList option')].map(o => o.value)
    .filter(v => !v.includes(',') && !token.used.has(v.trim().toLowerCase()) && v.toLowerCase().includes(query));
}
function _disposeActionLabels() {
  const ctx = _actionLabelContext;
  if (!ctx) return;
  _actionLabelContext = null;
  ctx.abort.abort(); ctx.hide();
}
function _startActionLabels() {
  _disposeActionLabels();
  const input = document.getElementById('m-label'), list = document.getElementById('m-label-options');
  const hint = document.getElementById('m-label-hint');
  const ctx = { input, list, modal: document.getElementById('modalOverlay'), uid: _actionLabelUid(),
    abort: new AbortController(), active: -1, composing: false, snapshot: '' };
  _actionLabelContext = ctx;
  const snapshot = () => JSON.stringify([input.value, input.selectionStart, input.selectionEnd]);
  ctx.hide = () => {
    list.replaceChildren(); list.hidden = true; hint.hidden = true; ctx.active = -1;
    input.setAttribute('aria-expanded', 'false'); input.removeAttribute('aria-activedescendant');
  };
  function choose(value, option) {
    if (!_actionLabelsCurrent(ctx) || ctx.composing || list.hidden || option.parentNode !== list
        || snapshot() !== ctx.snapshot) return;
    const token = _actionLabelToken(input);
    if (!_actionLabelMatches(token).includes(value)) { ctx.hide(); return; }
    // A whitespace-only token inserts at the caret; its whitespace is never counted twice.
    const first = token.token.search(/\S/);
    const left = first < 0 ? token.start : token.left + first;
    const right = first < 0 ? token.start : token.right - (token.token.match(/\s*$/)[0].length);
    input.value = token.value.slice(0, left) + value + token.value.slice(right);
    input.focus(); input.setSelectionRange(left + value.length, left + value.length);
    ctx.snapshot = snapshot(); ctx.hide();
  }
  ctx.refresh = (force = false) => {
    if (!_actionLabelsCurrent(ctx)) { if (_actionLabelContext === ctx) _disposeActionLabels(); return; }
    if (ctx.composing || document.activeElement !== input) { ctx.hide(); return; }
    const now = snapshot();
    if (!force && now === ctx.snapshot) return;
    ctx.snapshot = now; ctx.hide();
    const matches = _actionLabelMatches(_actionLabelToken(input));
    if (!matches.length) return;
    matches.slice(0, 8).forEach((value, i) => {
      const option = document.createElement('button');
      option.type = 'button'; option.tabIndex = -1; option.id = 'm-label-option-' + i;
      option.setAttribute('role', 'option'); option.setAttribute('aria-selected', 'false');
      option.textContent = value;
      option.onpointerdown = e => { if (e.button === 0) { e.preventDefault(); choose(value, option); } };
      option.onclick = e => { e.preventDefault(); choose(value, option); };
      list.appendChild(option);
    });
    list.hidden = false; hint.hidden = matches.length <= 8;
    input.setAttribute('aria-expanded', 'true');
  };
  const on = (target, type, fn) => target.addEventListener(type, fn, { signal: ctx.abort.signal });
  on(input, 'input', () => ctx.refresh(true));
  on(input, 'focus', () => ctx.refresh(true));
  on(input, 'click', () => ctx.refresh());
  on(input, 'select', () => ctx.refresh());
  on(document, 'selectionchange', () => { if (document.activeElement === input) ctx.refresh(); });
  // Keep the footer under the pointer until its click completes. Blurring here would
  // collapse the in-flow suggestions and move Add/Cancel between press and release.
  on(ctx.modal, 'pointerdown', e => {
    if (e.button === 0 && _actionLabelsCurrent(ctx) && document.activeElement === input
        && !list.hidden && e.target.closest('.modal-actions button')) e.preventDefault();
  });
  on(input, 'blur', ctx.hide);
  on(input, 'compositionstart', () => { ctx.composing = true; ctx.hide(); });
  on(input, 'compositionend', () => { ctx.composing = false; ctx.refresh(true); });
  on(input, 'keydown', e => {
    if (!_actionLabelsCurrent(ctx)) return;
    if (ctx.composing || e.isComposing || e.keyCode === 229) {
      // Let the IME consume Escape without the document's modal closer seeing it.
      if (e.key === 'Escape') e.stopPropagation();
      return;
    }
    if (e.key === 'Tab') { ctx.hide(); return; }
    if (e.key === 'Escape' && !list.hidden) { e.preventDefault(); e.stopPropagation(); ctx.hide(); return; }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (list.hidden) ctx.refresh(true);
      const options = [...list.children]; if (!options.length) return;
      e.preventDefault(); e.stopPropagation();
      ctx.active = ctx.active < 0 ? (e.key === 'ArrowDown' ? 0 : options.length - 1)
        : (ctx.active + (e.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
      options.forEach((o, i) => o.setAttribute('aria-selected', String(i === ctx.active)));
      input.setAttribute('aria-activedescendant', options[ctx.active].id);
      options[ctx.active].scrollIntoView({block:'nearest'});
    } else if (e.key === 'Enter' && !list.hidden && ctx.active >= 0) {
      e.preventDefault(); e.stopPropagation();
      const option = list.children[ctx.active]; choose(option.textContent, option);
    }
  });
}

/* ── MODAL ── */
function openModal() { document.getElementById('modalOverlay').classList.add('open'); updateLabelDatalist(); _startActionLabels(); document.getElementById('m-desc').focus(); }
function closeModal() {
  _disposeActionLabels();
  document.getElementById('modalOverlay').classList.remove('open');
  ['m-desc','m-owner','m-source','m-due','m-requestor','m-label','m-details'].forEach(id=>{const el=document.getElementById(id); if(el) el.value='';});
  document.getElementById('m-status').value='Not yet started';
  document.getElementById('m-priority').value='Medium';
  /* v2.90: clear sub-action parent context on modal close */
  pendingSubActionParentId = null;
  const banner = document.getElementById('subActionBanner');
  if (banner) banner.style.display = 'none';
}
function handleOverlayClick(e) { if(e.target===e.currentTarget) closeModal(); }

/* v2.90: when set to a parent action ID, the next submitNewAction will create a sub-action
   under that parent. Cleared by closeModal and submitNewAction (after use). */
let pendingSubActionParentId = null;

/* v2.90: open the new-action modal in sub-action mode with a purple banner showing the parent. */
function addSubActionFromButton(parentId) {
  if (typeof parentId !== 'number') return;
  const e = edits[parentId];
  if (!e) return;
  /* Guard: can only create sub-action under a top-level action (single-level rule) */
  if (e.parentId != null) {
    /* parent is itself a sub-action — refuse (defensive) */
    return;
  }
  pendingSubActionParentId = parentId;
  const banner = document.getElementById('subActionBanner');
  const text   = document.getElementById('subActionBannerText');
  if (banner && text) {
    const desc = (e.desc || '').replace(/\s+/g, ' ').trim();
    const trunc = desc.length > 70 ? desc.slice(0, 67) + '…' : desc;
    text.textContent = `Sub-action of ${displayActionId(parentId)}${trunc ? ': ' + trunc : ''}`;   /* v15.2: derived number */
    banner.style.display = 'flex';
  }
  openModal();
}

function submitNewAction() {
  const desc=document.getElementById('m-desc').value.trim();
  if(!desc){document.getElementById('m-desc').focus();return;}
  const today=todayISO(), id=_nextActSeq();   /* v16.8 (W5): records the high-water mark */
  /* v2.12: source no longer auto-prefilled with date; just whatever the user typed (or empty).
     New fields requestor, category, details are read from the modal. */
  const action={
    id, date:today,
    source:    document.getElementById('m-source').value.trim() || '',
    desc,
    owner:     document.getElementById('m-owner').value.trim() || '',
    requestor: document.getElementById('m-requestor').value.trim() || '',
    /* v2.40: labels parsed from comma-separated input */
    labels:    (document.getElementById('m-label').value.trim() ? document.getElementById('m-label').value.split(',').map(s=>s.trim()).filter(Boolean) : []),
    details:   document.getElementById('m-details').value.trim() || '',
    due:       document.getElementById('m-due').value || null,
    status:    document.getElementById('m-status').value,
    priority:  document.getElementById('m-priority').value,
    /* v2.90: sub-action parent (null for top-level) */
    parentId:  (typeof pendingSubActionParentId === 'number') ? pendingSubActionParentId : null,
    notes:     null
  };
  newActions.push(action);
  initActionEdits(action);
  markDirty();
  closeModal(); refreshAll();
  const card=document.getElementById('card-'+id);
  if(card) card.scrollIntoView({behavior:'smooth',block:'nearest'});
}


/* ── CONFIRM MODAL ── */
let pendingConfirm = null;
function showConfirm(title, message, onConfirm) {
  pendingConfirm = onConfirm;
  document.getElementById('confirmTitle').textContent = title;
  document.getElementById('confirmMessage').innerHTML = message;
  document.getElementById('confirmOverlay').classList.add('open');
  setTimeout(() => { const b = document.getElementById('confirmDeleteBtn'); if (b) b.focus(); }, 10);
}
function closeConfirm() {
  pendingConfirm = null;
  document.getElementById('confirmOverlay').classList.remove('open');
}
function executeConfirm() {
  const fn = pendingConfirm;
  closeConfirm();
  if (fn) fn();
}
function handleConfirmOverlayClick(e) { if (e.target === e.currentTarget) closeConfirm(); }

/* ── EXPORT ── */
/* Defensive sync — at save time, copy current DOM values into SESSIONS so the
   saved data always matches what the user sees on screen. Protects against any
   case where oninput might not have fired (e.g., textarea destroyed/recreated
   by renderSessions while typing, paste-without-input-event, IME composition,
   fast typing during DOM replacement, browser autofill, etc.). */
function syncSessionsFromDOM() {
  document.querySelectorAll('.session-card').forEach(card => {
    const id = Number(card.dataset.id);
    if (!Number.isFinite(id)) return;
    const s = SESSIONS.find(x => x.id === id);
    if (!s) return;

    const titleEl   = card.querySelector('.session-title-input');
    const dateEl    = card.querySelector('.session-date-input');
    const timeEls   = card.querySelectorAll('.time-input');
    const minutesEl = card.querySelector('.minutes-textarea');

    if (titleEl)   s.title     = titleEl.value;
    if (dateEl)    s.date      = dateEl.value;
    if (timeEls.length >= 1) s.startTime = timeEls[0].value;
    if (timeEls.length >= 2) s.endTime   = timeEls[1].value;
    if (minutesEl) s.minutes   = minutesEl.value;

    // Sync attendee rows from DOM
    const groups = card.querySelectorAll('.attendee-group');
    if (groups.length > 0) {
      const rows = [];
      groups.forEach(g => {
        const co = g.querySelector('.attendee-organisation');
        const nm = g.querySelector('.attendee-name');
        const pr = g.querySelector('.attendee-present');
        const op = g.querySelector('.att-opt');
        const row = { name: nm ? nm.value : '', organisation: co ? co.value : '' };
        if (pr && !pr.checked) row.present = false;   // v5.1 §3: keep absent flag (present is default → omitted)
        /* v16 (b): this block REBUILDS every row from the DOM, so a key not read here is
           silently destroyed on the next sync — which runs before save, before duplicate and
           before delete. Same lazy idiom as `present`: written only when set. */
        if (op && op.getAttribute('aria-pressed') === 'true') row.optional = true;
        rows.push(row);
      });
      s.attendeeRows = rows;
      s.attendees = formatAttendeesPreview(rows);
    }
  });
}

